import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import { usePhoneCallService } from '../../services/phoneCallService';
import { useCatalogService } from '../../services/catalogService';
import { useAccountService } from '../../services/accountService';
import { useSurveyService } from '../../services/surveyService';
import { useReferralService } from '../../services/referralService';
import CapturePatientModal, { type PatientCaptureData } from './CapturePatientModal';
import ClaimsModal from './ClaimsModal';
import ServiceSpecialistAssignmentModal from './ServiceSpecialistAssignmentModal';
import type { Specialist } from '../../types/phoneCapture';
import SurveyModal from './SurveyModal';
import MultiClientSurveyModal from './MultiClientSurveyModal';
import type {
  ClientSurveyAnswer,
  ClientSurveyRecord,
  ClientSurveyStatus,
  SurveyTemplateDetail
} from '../../types/survey';
import SeveritySemaphoreSelector from '../common/SeveritySemaphoreSelector';
import { DEFAULT_SEVERITY_LEVEL, parseSeverityLevel, type SeverityLevel } from '../../constants/severityLevel';
import { PhoneCallStatus } from '../../types/phoneCapture';
import { isEapServiceType, isHwbWellBeingCoachingServiceType } from '../../constants/productServiceTypes';
import {
  extractClaimsFromPhoneCall,
  type PhoneCallClaim
} from '../../utils/phoneCallClaims';
import { getAgeRangeFromAge, isValidAgeOneOrTwoDigits } from '../../utils/ageRange';

interface CaptureWizardModalProps {
  open: boolean;
  /** phoneCallId: Intake (borrador o finalizado) creado/abierto en el wizard, si existe. */
  onClose: (refreshData?: boolean, phoneCallId?: number | null) => void;
  /** Si se indica, carga esa llamada existente en el wizard (misma API que ver/editar). */
  initialPhoneCallId?: number | null;
  /** Solo consulta: sin edición ni acciones de guardado. */
  readOnly?: boolean;
  /**
   * Panel junto al chat de WhatsApp: no tapa la conversación.
   * Cerrar oculta el formulario y conserva el borrador.
   */
  embedded?: boolean;
  /** Avisa en cuanto existe el id del borrador, antes de cerrar el panel. */
  onDraftReady?: (phoneCallId: number) => void;
  /** Prefill desde WhatsApp (nuevo Intake). */
  whatsappPrefill?: {
    accountId?: string;
    accountName?: string;
    lockAccount?: boolean;
    mobilePhone?: string;
    firstName?: string;
    lastName?: string;
    middleName?: string;
    email?: string;
    city?: string;
  } | null;
}

interface EmployeeFormState {
  firstName: string;
  lastName: string;
  middleName: string;
  age: string;
  gender: string;
  city: string;
  accountId: string;
  email: string;
  mobilePhone: string;
  homePhone: string;
  position: string;
  lengthOfService: string;
  maritalStatus: string;
}

interface PersistedClient {
  key: string;
  draftClientId: number;
  relationship: string;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
}

/** Servicio de crisis pendiente de completar (solo frontend hasta persistir el referral). */
interface CrisisPendingService {
  localId: string;
  serviceType: string;
  preDiagnosticNotes: string;
}

const initialEmployeeForm: EmployeeFormState = {
  firstName: '',
  lastName: '',
  middleName: '',
  age: '',
  gender: '',
  city: '',
  accountId: '',
  email: '',
  mobilePhone: '',
  homePhone: '',
  position: '',
  lengthOfService: '',
  maritalStatus: ''
};

/** Serializa empleado + origen de referido para detectar cambios pendientes de guardar. */
function serializeEmployeeDraftSnapshot(
  form: EmployeeFormState,
  referralSource: string
): string {
  return JSON.stringify({ ...form, referralSourceIntake: referralSource });
}

const phoneDigitsOnly = (s: string) => (s || '').replace(/\D/g, '');
const isValidPhoneFormat = (s: string) => phoneDigitsOnly(s).length === 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmailFormat = (s: string) => EMAIL_REGEX.test((s || '').trim());

const extractPhoneCallRoot = (data: unknown): Record<string, unknown> =>
  ((data as { phonecall?: Record<string, unknown> }).phonecall || data) as Record<string, unknown>;

/** Modo rápido: al menos dos clientes EAP a encuestar. */
const QUICK_SURVEY_MIN_EAP_CLIENTS = 2;

const collectEapSurveyClientIds = (rows: PatientCaptureData[]): number[] =>
  Array.from(
    new Set(
      rows
        .filter((p) => isEapServiceType(p.serviceType))
        .map((p) => p.draftClientId)
        .filter((id): id is number => typeof id === 'number' && id > 0)
    )
  );

const getSurveyClientStatusChip = (entry?: {
  status?: ClientSurveyStatus;
  answers?: Record<number, ClientSurveyAnswer> | ClientSurveyAnswer[] | null;
} | null): { label: string; color: 'success' | 'warning' | 'info' | 'default' } => {
  if (entry?.status === 'SUBMITTED') return { label: 'Completada', color: 'success' };
  if (entry?.status === 'DECLINED') return { label: 'Declinada', color: 'warning' };
  if (Object.keys(entry?.answers || {}).length > 0) return { label: 'Parcial', color: 'info' };
  return { label: 'Pendiente', color: 'default' };
};

const getRelationshipLabel = (relationship: string, otherDescription?: string) => {
  if (relationship === 'Other') {
    const detail = (otherDescription || '').trim();
    return detail ? `Otro (${detail})` : 'Otro';
  }
  switch (relationship) {
    case 'Employee':
      return 'Empleado';
    case 'Partner':
      return 'Pareja';
    case 'Child':
      return 'Hijo(a)';
    default:
      return relationship || 'N/D';
  }
};

/** Mapeo relación del modal → `employeeRelationship` del draft (contrato phonecall-draft-contract) */
const RELATIONSHIP_TO_DRAFT_API: Record<string, string> = {
  Employee: 'EMPLOYEE',
  Partner: 'SPOUSE',
  Child: 'CHILD',
  Other: 'OTHER'
};

const employeeTypeDefaultFromRelationship = (relationship: string): string => {
  if (relationship === 'Child' || relationship === 'Partner') return 'DEPENDENT';
  return 'ACTIVE';
};

const draftCallDateToday = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const CDMX_TIME_ZONE = 'America/Mexico_City';

const formatHeaderCallDateCdmx = (d: Date): string => {
  return d.toLocaleDateString('es-MX', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: CDMX_TIME_ZONE
  });
};

const formatHeaderTimeCdmx = (d: Date): string => {
  return d.toLocaleTimeString('es-MX', {
    timeZone: CDMX_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const formatDurationHMS = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

function extractDraftClientReferralIds(
  response: unknown,
  firstName: string,
  lastName: string,
  middleName: string,
  relationship: string,
  email: string,
  product: string
): { clientId?: number; referralId?: number } {
  const root = response as { phonecall?: { patients?: Array<Record<string, unknown>> } };
  const list = root?.phonecall?.patients ?? [];
  const privateFirst = (firstName || '').trim().toLowerCase();
  const privateLast = (lastName || '').trim().toLowerCase();
  const privateMiddle = (middleName || '').trim().toLowerCase();
  const relCode = (RELATIONSHIP_TO_DRAFT_API[relationship] || relationship || '').trim().toUpperCase();
  const mail = (email || '').trim().toLowerCase();

  const candidates = list.filter((p) => {
    const pRel = String(p.employeeRelationship || '').trim().toUpperCase();
    if (relCode && pRel && pRel !== relCode) return false;

    const pPrivateFirst = String(p.privateFirstName || '').trim().toLowerCase();
    const pPrivateLast = String(p.privateLastName || '').trim().toLowerCase();
    const pPrivateMiddle = String(p.privateMiddleName || '').trim().toLowerCase();

    if (privateFirst && pPrivateFirst && pPrivateFirst !== privateFirst) return false;
    if (privateLast && pPrivateLast && pPrivateLast !== privateLast) return false;
    if (privateMiddle && pPrivateMiddle && pPrivateMiddle !== privateMiddle) return false;

    const pEmail = String(p.email || '').trim().toLowerCase();
    if (mail && pEmail && pEmail !== mail) return false;

    return true;
  });

  const sortedCandidates = [...candidates].sort(
    (a, b) => Number(b.id || 0) - Number(a.id || 0)
  );

  for (const p of sortedCandidates) {
    const refs = (p.referrals as Array<Record<string, unknown>> | undefined) ?? [];
    const matchingReferrals = refs.filter((r) => String(r.product || '') === String(product || ''));
    const referralPool = matchingReferrals.length > 0 ? matchingReferrals : refs;
    const ref = [...referralPool].sort((a, b) => Number(b.id || 0) - Number(a.id || 0))[0] || null;
    const pid = p.id as number | undefined;
    const rid = ref?.id as number | undefined;
    if (typeof pid === 'number' && ref && typeof rid === 'number') {
      return { clientId: pid, referralId: rid };
    }
  }
  return {};
}

function getPatientPersonKey(data: Pick<PatientCaptureData, 'relationship' | 'firstName' | 'lastName' | 'middleName' | 'email'>): string {
  return [
    String(data.relationship || '').trim().toUpperCase(),
    String(data.firstName || '').trim().toLowerCase(),
    String(data.lastName || '').trim().toLowerCase(),
    String(data.middleName || '').trim().toLowerCase(),
    String(data.email || '').trim().toLowerCase()
  ].join('|');
}

function getPersistedClientKey(data: Pick<PersistedClient, 'relationship' | 'firstName' | 'lastName' | 'middleName' | 'email'>): string {
  return [
    String(data.relationship || '').trim().toUpperCase(),
    String(data.firstName || '').trim().toLowerCase(),
    String(data.lastName || '').trim().toLowerCase(),
    String(data.middleName || '').trim().toLowerCase(),
    String(data.email || '').trim().toLowerCase()
  ].join('|');
}

function extractReferralIdFromClient(
  response: unknown,
  clientId: number,
  product: string
): number | null {
  const root = response as { phonecall?: { patients?: Array<Record<string, unknown>> } };
  const list = root?.phonecall?.patients ?? [];
  const client = list.find((p) => Number(p.id || 0) === Number(clientId));
  if (!client) return null;
  const refs = (client.referrals as Array<Record<string, unknown>> | undefined) ?? [];
  if (refs.length === 0) return null;
  const byProduct = refs.filter((r) => String(r.product || '') === String(product || ''));
  const pool = byProduct.length > 0 ? byProduct : refs;
  const last = [...pool].sort((a, b) => Number(b.id || 0) - Number(a.id || 0))[0];
  return typeof last?.id === 'number' ? (last.id as number) : null;
}

function buildDraftClientPayload(data: PatientCaptureData): Record<string, unknown> {
  const firstName = (data.firstName || '').trim();
  const lastName = (data.lastName || '').trim();
  const middleName = (data.middleName || '').trim();

  const client: Record<string, unknown> = {
    firstName: firstName ? firstName.charAt(0).toUpperCase() : '',
    lastName: lastName ? lastName.charAt(0).toUpperCase() : '',
    privateFirstName: firstName || undefined,
    privateLastName: lastName || undefined,
    privateMiddleName: middleName || undefined,
    employeeRelationship:
      RELATIONSHIP_TO_DRAFT_API[data.relationship] ??
      String(data.relationship || '').toUpperCase()
  };
  if (data.relationship === 'Other') {
    const description = (data.otherRelationshipDescription || '').trim();
    if (description) client.employeeRelationshipDescription = description;
  }
  if (data.email.trim()) client.email = data.email.trim();
  if (data.mobilePhone.trim()) client.mobilePhone = data.mobilePhone.trim();
  if (typeof data.gender === 'string' && data.gender.trim() !== '') {
    client.gender = data.gender.trim();
  }
  if (data.age && isValidAgeOneOrTwoDigits(data.age)) {
    client.age = parseInt(data.age, 10);
  }
  if (isEapServiceType(data.serviceType)) {
    client.privatePreviousPsychologicalTreatment = Boolean(data.hasPriorPsychologicalTreatment);
  }
  return client;
}

function applyClientTreatmentFieldsForExistingClient(
  clientId: number,
  data: PatientCaptureData
): Record<string, unknown> {
  const base: Record<string, unknown> = { id: clientId };
  if (data.age && isValidAgeOneOrTwoDigits(data.age)) {
    base.age = parseInt(data.age, 10);
  }
  if (!isEapServiceType(data.serviceType)) return base;
  return {
    ...base,
    privatePreviousPsychologicalTreatment: Boolean(data.hasPriorPsychologicalTreatment)
  };
}

function buildDraftReferralPayload(
  data: PatientCaptureData,
  catalogs: Record<string, Array<{ code: string; description: string }>>
): Record<string, unknown> {
  const delivery =
    (data.deliveryMethod || '').trim() ||
    catalogs.DELIVERY_METHOD?.[0]?.code ||
    'PHONE';
  const risk = catalogs.RISK_TYPE?.[0]?.code ?? 'LOW';
  const empType =
    catalogs.EMPLOYEE_TYPE?.[0]?.code ?? employeeTypeDefaultFromRelationship(data.relationship);
  const computedFromAge = getAgeRangeFromAge(data.age, catalogs.AGE_RANGE || []);
  const ageRange =
    computedFromAge ||
    (data.ageRange || '').trim() ||
    catalogs.AGE_RANGE?.[0]?.code ||
    '26-35';

  return {
    product: data.serviceType,
    deliveryMethod: delivery,
    comments: (data.preDiagnosticNotes || '').trim() || undefined,
    riskType: risk,
    employeeType: empType,
    ageRange,
    callDate: draftCallDateToday(),
    origin: 'PHONE_CALL',
    ...(isHwbWellBeingCoachingServiceType(data.serviceType)
      ? { pregnancyCoaching: data.pregnancyCoaching === true }
      : {}),
    ...(typeof data.assignedSpecialistId === 'number'
      ? { assignedSpecialistId: data.assignedSpecialistId }
      : {})
  };
}

function resolveReferralSourceIntakeFromPhoneCall(root: Record<string, unknown>): string {
  const fromCall = String(root.referralSourceIntake || '').trim();
  if (fromCall) return fromCall;

  const patientsArr = root.patients as unknown[] | undefined;
  if (Array.isArray(patientsArr)) {
    for (const p of patientsArr) {
      const refs = (p as Record<string, unknown>).referrals as unknown[] | undefined;
      if (!Array.isArray(refs)) continue;
      for (const ref of refs) {
        const legacy = String((ref as Record<string, unknown>).referralSourceIntake || '').trim();
        if (legacy) return legacy;
      }
    }
  }

  const legacyReferral = root.referral as Record<string, unknown> | undefined;
  if (legacyReferral) {
    const legacy = String(legacyReferral.referralSourceIntake || '').trim();
    if (legacy) return legacy;
  }

  return '';
}

function buildDraftEmployeePayload(
  employeeForm: EmployeeFormState,
  draftEmployeeId: number | null
): Record<string, unknown> {
  const employeePayload: Record<string, unknown> = {
    email: employeeForm.email.trim(),
    firstName: employeeForm.firstName.trim(),
    lastName: employeeForm.lastName.trim(),
    middleName: employeeForm.middleName.trim(),
    accountId: Number(employeeForm.accountId.trim()),
    mobilePhone: employeeForm.mobilePhone || '',
    familyMembers: []
  };

  if (draftEmployeeId != null) employeePayload.id = draftEmployeeId;
  if (employeeForm.age.trim() !== '' && isValidAgeOneOrTwoDigits(employeeForm.age)) {
    employeePayload.age = parseInt(employeeForm.age, 10);
  }
  if (employeeForm.gender) employeePayload.gender = employeeForm.gender;
  if (employeeForm.city.trim() !== '') employeePayload.city = employeeForm.city.trim();
  if (employeeForm.position.trim() !== '') employeePayload.position = employeeForm.position.trim();
  if (employeeForm.lengthOfService) employeePayload.lengthOfService = employeeForm.lengthOfService;
  if (employeeForm.maritalStatus) employeePayload.maritalStatus = employeeForm.maritalStatus;
  if (employeeForm.homePhone.trim() !== '') employeePayload.homePhone = employeeForm.homePhone;

  return employeePayload;
}

function mapDraftRelationshipToUi(rel: string): 'Employee' | 'Partner' | 'Child' | 'Other' {
  const u = (rel || '').trim().toUpperCase();
  if (u === 'EMPLOYEE') return 'Employee';
  if (u === 'SPOUSE') return 'Partner';
  if (u === 'CHILD') return 'Child';
  if (u === 'OTHER') return 'Other';
  if (rel === 'Employee' || rel === 'Partner' || rel === 'Child' || rel === 'Other') {
    return rel as 'Employee' | 'Partner' | 'Child' | 'Other';
  }
  return 'Other';
}

function mapClientAndReferralToPatientCapture(
  client: Record<string, unknown>,
  referral: Record<string, unknown> | null | undefined
): PatientCaptureData {
  const rel = mapDraftRelationshipToUi(String(client.employeeRelationship || ''));
  const ref = referral || undefined;
  const cid = client.id != null ? Number(client.id) : NaN;
  const rid = ref && ref.id != null ? Number(ref.id) : NaN;
  const product = String((ref as { product?: string } | undefined)?.product || '');
  return {
    firstName: String(client.privateFirstName || client.firstName || ''),
    lastName: String(client.privateLastName || client.lastName || ''),
    middleName: String(client.privateMiddleName || client.middleName || ''),
    email: String(client.email || ''),
    mobilePhone: String(client.mobilePhone || client.privateMobilePhone || ''),
    gender: String(client.gender || ''),
    age: client.age != null && String(client.age).trim() !== '' ? String(client.age) : '',
    ageRange: String((ref as { ageRange?: string } | undefined)?.ageRange || ''),
    relationship: rel,
    otherRelationshipDescription: String(
      client.employeeRelationshipDescription || client.otherRelationshipDescription || ''
    ),
    serviceType: product,
    deliveryMethod: String((ref as { deliveryMethod?: string } | undefined)?.deliveryMethod || ''),
    pregnancyCoaching: isHwbWellBeingCoachingServiceType(product)
      ? (ref as { pregnancyCoaching?: boolean | null } | undefined)?.pregnancyCoaching === true
      : null,
    preDiagnosticNotes: String(
      (ref as { presentingIssue?: string; comments?: string } | undefined)?.presentingIssue ||
        (ref as { comments?: string } | undefined)?.comments ||
        ''
    ),
    hasPriorPsychologicalTreatment: Boolean(client.privatePreviousPsychologicalTreatment),
    assignedSpecialistId:
      typeof (ref as { assignedSpecialistId?: number } | undefined)?.assignedSpecialistId === 'number'
        ? (ref as { assignedSpecialistId: number }).assignedSpecialistId
        : typeof client.assignedSpecialistId === 'number'
          ? (client.assignedSpecialistId as number)
          : null,
    assignedSpecialistName:
      ((ref as { assignedSpecialistName?: string } | undefined)?.assignedSpecialistName as string | undefined) ||
      ((client as { assignedSpecialistName?: string }).assignedSpecialistName as string | undefined) ||
      null,
    assignmentDate: (() => {
      const fromRef = (ref as { assignmentDate?: string } | undefined)?.assignmentDate;
      if (fromRef) return String(fromRef);
      const fromClient = (client as { assignmentDate?: string }).assignmentDate;
      return fromClient ? String(fromClient) : null;
    })(),
    draftClientId: Number.isFinite(cid) ? cid : null,
    draftReferralId: Number.isFinite(rid) ? rid : null,
    externalReferralId:
      typeof (ref as { externalReferralId?: number } | undefined)?.externalReferralId === 'number'
        ? (ref as { externalReferralId: number }).externalReferralId
        : null,
    readyForSync: Boolean((ref as { readyForSync?: boolean } | undefined)?.readyForSync)
  };
}

function extractExternalCaseNumbers(root: Record<string, unknown>): Map<number, number> {
  const result = new Map<number, number>();
  const clients = Array.isArray(root.patients) ? root.patients : [];

  clients.forEach((client) => {
    const referrals = Array.isArray((client as Record<string, unknown>).referrals)
      ? ((client as Record<string, unknown>).referrals as unknown[])
      : [];
    referrals.forEach((referral) => {
      const data = referral as Record<string, unknown>;
      if (data.id == null || data.externalReferralId == null) return;
      const internalId = Number(data.id);
      const externalId = Number(data.externalReferralId);
      if (Number.isFinite(internalId) && Number.isFinite(externalId)) {
        result.set(internalId, externalId);
      }
    });
  });

  return result;
}

function buildPersistedClientsFromPatients(rows: PatientCaptureData[]): PersistedClient[] {
  return rows
    .filter((r) => typeof r.draftClientId === 'number' && r.draftClientId != null && r.draftClientId > 0)
    .map((r) => ({
      key: `${r.draftClientId}-${r.relationship}-${r.firstName}-${r.lastName}`,
      draftClientId: r.draftClientId as number,
      relationship: r.relationship,
      firstName: r.firstName,
      lastName: r.lastName,
      middleName: r.middleName || '',
      email: r.email || ''
    }));
}

function createCrisisPendingService(notes: string): CrisisPendingService {
  return {
    localId: crypto.randomUUID(),
    serviceType: 'EAP',
    preDiagnosticNotes: notes.trim()
  };
}

const IntakeShell: React.FC<{
  embedded: boolean;
  open: boolean;
  onDismiss: () => void;
  children: React.ReactNode;
}> = ({ embedded, open, onDismiss, children }) => {
  if (embedded) {
    return (
      <Paper
        elevation={1}
        sx={{
          flex: 1,
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {children}
      </Paper>
    );
  }
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          onDismiss();
          return;
        }
        onDismiss();
      }}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
    >
      {children}
    </Dialog>
  );
};

const CaptureWizardModal: React.FC<CaptureWizardModalProps> = ({
  open,
  onClose,
  initialPhoneCallId = null,
  readOnly = false,
  embedded = false,
  onDraftReady,
  whatsappPrefill = null,
}) => {
  const { getPrivacyNotice, createPhoneCallDraft, updatePhoneCallDraft, finalizePhoneCallDraft, assignSpecialistToDraftReferral, getPhoneCallById, updatePhoneCallSeverityLevel } =
    usePhoneCallService();
  const { markReferralsReadyForSync } = useReferralService();
  const { getSurveyTemplates, getSurveyTemplateById, getClientSurveysByPhoneCall, batchSaveClientSurveys } =
    useSurveyService();
  const { getAllCatalogs } = useCatalogService();
  const { getAvailableAccounts } = useAccountService();

  const [privacyLoading, setPrivacyLoading] = useState(true);
  const [privacyText, setPrivacyText] = useState('');
  const [privacyVersion, setPrivacyVersion] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogs, setCatalogs] = useState<Record<string, Array<{ code: string; description: string }>>>({});
  const [companies, setCompanies] = useState<
    Array<{ id?: number; orgId?: string; orgPath?: string; sessionCount?: number; isActive?: boolean }>
  >([]);
  const [employeeForm, setEmployeeForm] = useState<EmployeeFormState>(initialEmployeeForm);
  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState(false);
  const [privacyFullScreenOpen, setPrivacyFullScreenOpen] = useState(false);
  const [privacyModalOpenedOnce, setPrivacyModalOpenedOnce] = useState(false);
  const [capturePatientModalOpen, setCapturePatientModalOpen] = useState(false);
  const [serviceDetailViewOpen, setServiceDetailViewOpen] = useState(false);
  const [viewingPatientIndex, setViewingPatientIndex] = useState<number | null>(null);
  const [patients, setPatients] = useState<PatientCaptureData[]>([]);
  const [editingPatientIndex, setEditingPatientIndex] = useState<number | null>(null);
  const [patientValidationError, setPatientValidationError] = useState('');
  const [serviceSyncError, setServiceSyncError] = useState('');
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assigningServiceIndex, setAssigningServiceIndex] = useState<number | null>(null);
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [multiSurveyModalOpen, setMultiSurveyModalOpen] = useState(false);
  const [surveySaving, setSurveySaving] = useState(false);
  const [surveyError, setSurveyError] = useState('');
  const [surveyTemplate, setSurveyTemplate] = useState<SurveyTemplateDetail | null>(null);
  const [selectedSurveyClientId, setSelectedSurveyClientId] = useState<number | null>(null);
  const [clientSurveysByClientId, setClientSurveysByClientId] = useState<
    Record<number, { clientSurveyId: number | null; status: ClientSurveyStatus; answers: Record<number, ClientSurveyAnswer> }>
  >({});
  const [draftPhoneCallId, setDraftPhoneCallId] = useState<number | null>(null);
  const [draftCreationError, setDraftCreationError] = useState<string>('');
  const [headerReferenceDate, setHeaderReferenceDate] = useState<Date | null>(null);
  /** Duración persistida en servidor (HH:MM:SS), al cargar ver/editar desde listado. */
  const [savedPhoneCallDuration, setSavedPhoneCallDuration] = useState('');
  const [callElapsedSeconds, setCallElapsedSeconds] = useState(0);
  const [finishCallSaving, setFinishCallSaving] = useState(false);
  const [finishCallError, setFinishCallError] = useState('');
  const [confirmFinishUnchannelizedOpen, setConfirmFinishUnchannelizedOpen] = useState(false);
  const [confirmCaseReadyOpen, setConfirmCaseReadyOpen] = useState(false);
  const [caseReadyReferralId, setCaseReadyReferralId] = useState<number | null>(null);
  const [caseReadySaving, setCaseReadySaving] = useState(false);
  const [caseReadyError, setCaseReadyError] = useState('');
  const [privacyUpdateError, setPrivacyUpdateError] = useState<string>('');
  const [privacyUpdating, setPrivacyUpdating] = useState(false);
  const [severityLevel, setSeverityLevel] = useState<SeverityLevel>(DEFAULT_SEVERITY_LEVEL);
  const [severityUpdateError, setSeverityUpdateError] = useState('');
  const [severityUpdating, setSeverityUpdating] = useState(false);
  const [phoneCallStatus, setPhoneCallStatus] = useState<string>(PhoneCallStatus.DRAFT);
  const [draftEmployeeId, setDraftEmployeeId] = useState<number | null>(null);
  const [employeeUpdateError, setEmployeeUpdateError] = useState('');
  const [employeeUpdating, setEmployeeUpdating] = useState(false);
  const [employeeSavedToDraft, setEmployeeSavedToDraft] = useState(false);
  /** Snapshot del último guardado exitoso; null = aún no se ha persistido en esta sesión. */
  const [employeeSavedSnapshot, setEmployeeSavedSnapshot] = useState<string | null>(null);
  const [referralSourceIntake, setReferralSourceIntake] = useState('');
  const [crisisModalOpen, setCrisisModalOpen] = useState(false);
  const [crisisNotes, setCrisisNotes] = useState('');
  const [hadCrisis, setHadCrisis] = useState(false);
  const [crisisDraftText, setCrisisDraftText] = useState('');
  const [crisisSaving, setCrisisSaving] = useState(false);
  const [crisisSaveError, setCrisisSaveError] = useState('');
  const [crisisPendingService, setCrisisPendingService] = useState<CrisisPendingService | null>(null);
  const [completingCrisisStub, setCompletingCrisisStub] = useState(false);
  const [persistedClients, setPersistedClients] = useState<PersistedClient[]>([]);
  const [referralClaims, setReferralClaims] = useState<PhoneCallClaim[]>([]);
  const [claimsByReferralId, setClaimsByReferralId] = useState<Record<number, PhoneCallClaim[]>>({});
  const [claimsModalOpen, setClaimsModalOpen] = useState(false);
  const [claimsModalReferralId, setClaimsModalReferralId] = useState<number | null>(null);
  const [finishCallSuccess, setFinishCallSuccess] = useState('');
  const [finishCallEmailWarnings, setFinishCallEmailWarnings] = useState<string[]>([]);
  const [refreshDashboardOnClose, setRefreshDashboardOnClose] = useState(false);
  const servicesSectionRef = useRef<HTMLDivElement | null>(null);
  const draftCreationRequestedRef = useRef(false);
  const callTimerIntervalRef = useRef<number | null>(null);
  /** Inicio local de la sesión del wizard (para duración si el contador por intervalo falla). */
  const callSessionStartedAtRef = useRef<number | null>(null);
  const employeeAutoInitialSaveAttemptedRef = useRef(false);
  const onDraftReadyRef = useRef(onDraftReady);
  onDraftReadyRef.current = onDraftReady;

  useEffect(() => {
    if (!open) {
      draftCreationRequestedRef.current = false;
      setDraftPhoneCallId(null);
      setDraftCreationError('');
      setHeaderReferenceDate(null);
      setSavedPhoneCallDuration('');
      setCallElapsedSeconds(0);
      callSessionStartedAtRef.current = null;
      setFinishCallSaving(false);
      setFinishCallError('');
      setPrivacyVersion('');
      setPrivacyUpdateError('');
      setPrivacyUpdating(false);
      setSeverityLevel(DEFAULT_SEVERITY_LEVEL);
      setSeverityUpdateError('');
      setSeverityUpdating(false);
      setPhoneCallStatus(PhoneCallStatus.DRAFT);
      setDraftEmployeeId(null);
      setEmployeeUpdateError('');
      setEmployeeUpdating(false);
      setEmployeeSavedToDraft(false);
      setEmployeeSavedSnapshot(null);
      setReferralSourceIntake('');
      setCrisisModalOpen(false);
      setCrisisNotes('');
      setHadCrisis(false);
      setCrisisDraftText('');
      setCrisisSaving(false);
      setCrisisSaveError('');
      setCrisisPendingService(null);
      setCompletingCrisisStub(false);
      employeeAutoInitialSaveAttemptedRef.current = false;
      setServiceSyncError('');
      setPersistedClients([]);
      setSurveySaving(false);
      setSurveyError('');
      setSurveyTemplate(null);
      setSelectedSurveyClientId(null);
      setClientSurveysByClientId({});
      setMultiSurveyModalOpen(false);
      setServiceDetailViewOpen(false);
      setViewingPatientIndex(null);
      return;
    }

    if (draftCreationRequestedRef.current) return;

    draftCreationRequestedRef.current = true;

    const existingId =
      typeof initialPhoneCallId === 'number' && initialPhoneCallId > 0 ? initialPhoneCallId : null;

    const loadExistingPhoneCall = async () => {
      try {
        setDraftCreationError('');
        const phoneCallData = await getPhoneCallById(existingId!);
        const root = ((phoneCallData as { phonecall?: Record<string, unknown> }).phonecall ||
          phoneCallData) as Record<string, unknown>;

        const resolvedId =
          typeof root.id === 'number' && Number.isFinite(root.id) ? (root.id as number) : existingId!;
        setDraftPhoneCallId(resolvedId);
        onDraftReadyRef.current?.(resolvedId);

        const durRaw = root.duration;
        setSavedPhoneCallDuration(
          durRaw != null && String(durRaw).trim() !== '' ? String(durRaw).trim() : ''
        );

        const callDateStr = root.callDate as string | undefined;
        if (callDateStr) {
          const parsed = new Date(String(callDateStr));
          if (!Number.isNaN(parsed.getTime())) setHeaderReferenceDate(parsed);
        } else {
          setHeaderReferenceDate(new Date());
        }

        setPrivacyAccepted(!!root.privacyConsent);
        setPrivacyVersion(String(root.privacyVersion || ''));
        if (root.privacyConsent) setPrivacyModalOpenedOnce(true);

        const loadedReferralSource = resolveReferralSourceIntakeFromPhoneCall(root);
        setReferralSourceIntake(loadedReferralSource);
        const loadedCrisisNotes = String(root.crisisNotes || '');
        setCrisisNotes(loadedCrisisNotes);
        setHadCrisis(Boolean(root.hadCrisis) || loadedCrisisNotes.trim() !== '');

        const status = String(root.status || PhoneCallStatus.DRAFT);
        setPhoneCallStatus(status);

        const savedSeverity = parseSeverityLevel(root.severityLevel);
        setSeverityLevel(savedSeverity ?? DEFAULT_SEVERITY_LEVEL);
        if (savedSeverity == null && status === PhoneCallStatus.DRAFT && !readOnly) {
          void updatePhoneCallSeverityLevel(resolvedId, DEFAULT_SEVERITY_LEVEL)
            .then((response) => {
              const persisted = parseSeverityLevel(extractPhoneCallRoot(response).severityLevel);
              if (persisted) setSeverityLevel(persisted);
            })
            .catch(() => {
              /* el valor por defecto queda en UI aunque falle el guardado */
            });
        }

        const emp = root.employee as Record<string, unknown> | undefined;
        if (emp) {
          const eid = emp.id != null ? Number(emp.id) : NaN;
          setDraftEmployeeId(Number.isFinite(eid) ? eid : null);
          const loadedEmployeeForm: EmployeeFormState = {
            firstName: String(emp.firstName || ''),
            lastName: String(emp.lastName || ''),
            middleName: String(emp.middleName || ''),
            age:
              emp.age != null && String(emp.age).trim() !== '' ? String(emp.age) : '',
            gender: String(emp.gender || ''),
            city: String(emp.city || ''),
            accountId: emp.accountId != null ? String(emp.accountId) : '',
            email: String(emp.email || ''),
            mobilePhone: String(emp.mobilePhone || ''),
            homePhone: String(emp.homePhone || ''),
            position: String(emp.position || ''),
            lengthOfService: String(emp.lengthOfService || ''),
            maritalStatus: String(emp.maritalStatus || '')
          };
          setEmployeeForm(loadedEmployeeForm);
          if (emp.accountId != null) {
            const accountId = Number(emp.accountId);
            setCompanies(currentCompanies => {
              const accountExists = currentCompanies.some(
                company => Number(company.id || company.orgId) === accountId
              );
              if (accountExists) return currentCompanies;
              return [
                ...currentCompanies,
                {
                  id: accountId,
                  orgPath: `${String(emp.accountOrgPath || accountId)} (Inactiva)`,
                  isActive: false
                }
              ];
            });
          }
          setEmployeeSavedToDraft(true);
          setEmployeeSavedSnapshot(
            serializeEmployeeDraftSnapshot(loadedEmployeeForm, loadedReferralSource)
          );
        } else {
          setDraftEmployeeId(null);
          setEmployeeForm(
            whatsappPrefill
              ? {
                  ...initialEmployeeForm,
                  accountId: whatsappPrefill.accountId ?? '',
                  mobilePhone: whatsappPrefill.mobilePhone ?? '',
                  firstName: whatsappPrefill.firstName ?? '',
                  lastName: whatsappPrefill.lastName ?? '',
                  middleName: whatsappPrefill.middleName ?? '',
                  email: whatsappPrefill.email ?? '',
                  city: whatsappPrefill.city ?? '',
                }
              : initialEmployeeForm
          );
          setEmployeeSavedToDraft(false);
          setEmployeeSavedSnapshot(null);
        }

        const rows: PatientCaptureData[] = [];
        const patientsArr = root.patients as unknown[] | undefined;
        if (Array.isArray(patientsArr) && patientsArr.length > 0) {
          for (const p of patientsArr) {
            const pr = p as Record<string, unknown>;
            const refs = (pr.referrals as unknown[]) || [];
            if (refs.length > 0) {
              refs.forEach((ref) => {
                rows.push(mapClientAndReferralToPatientCapture(pr, ref as Record<string, unknown>));
              });
            } else {
              rows.push(mapClientAndReferralToPatientCapture(pr, undefined));
            }
          }
        } else {
          const p = root.patient as Record<string, unknown> | undefined | null;
          if (p) {
            const refs = (p.referrals as unknown[]) || [];
            if (refs.length > 0) {
              refs.forEach((ref) => {
                rows.push(mapClientAndReferralToPatientCapture(p, ref as Record<string, unknown>));
              });
            } else {
              const refFall = root.referral as Record<string, unknown> | undefined;
              rows.push(mapClientAndReferralToPatientCapture(p, refFall));
            }
          }
        }
        setPatients(rows);
        setPersistedClients(buildPersistedClientsFromPatients(rows));
        applyClaimsFromRoot(root);
        setCrisisPendingService(null);
      } catch (error) {
        setDraftCreationError(
          error instanceof Error
            ? error.message
            : 'No se pudo cargar la llamada. Cierra e intenta de nuevo.'
        );
      }
    };

    const createDraft = async () => {
      try {
        setDraftCreationError('');
        const response = await createPhoneCallDraft({});

        const createdId = response?.phonecall?.id;
        if (typeof createdId !== 'number') {
          throw new Error('La respuesta no devolvió un id de llamada válido.');
        }

        setDraftPhoneCallId(createdId);
        onDraftReadyRef.current?.(createdId);
        setPhoneCallStatus(PhoneCallStatus.DRAFT);
        setSeverityLevel(DEFAULT_SEVERITY_LEVEL);
        void updatePhoneCallSeverityLevel(createdId, DEFAULT_SEVERITY_LEVEL)
          .then((response) => {
            const persisted = parseSeverityLevel(extractPhoneCallRoot(response).severityLevel);
            if (persisted) setSeverityLevel(persisted);
          })
          .catch(() => {
            /* el valor por defecto queda en UI aunque falle el guardado */
          });
        const serverCallDate = response?.phonecall?.callDate;
        if (serverCallDate) {
          const parsed = new Date(String(serverCallDate));
          if (!Number.isNaN(parsed.getTime())) {
            setHeaderReferenceDate(parsed);
          }
        }
      } catch {
        setDraftCreationError('No se pudo crear el borrador de la llamada. Cierra y vuelve a intentar.');
      }
    };

    if (existingId != null) {
      setHeaderReferenceDate(null);
      void loadExistingPhoneCall();
    } else {
      setHeaderReferenceDate(new Date());
      if (whatsappPrefill) {
        setEmployeeForm({
          ...initialEmployeeForm,
          accountId: whatsappPrefill.accountId ?? '',
          mobilePhone: whatsappPrefill.mobilePhone ?? '',
          firstName: whatsappPrefill.firstName ?? '',
          lastName: whatsappPrefill.lastName ?? '',
          middleName: whatsappPrefill.middleName ?? '',
          email: whatsappPrefill.email ?? '',
        });
      }
      void createDraft();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- solo open / initialPhoneCallId / prefill (evita bucle con funciones del hook)
  }, [open, initialPhoneCallId, whatsappPrefill?.accountId, whatsappPrefill?.mobilePhone]);

  useEffect(() => {
    if (!open) {
      if (callTimerIntervalRef.current !== null) {
        window.clearInterval(callTimerIntervalRef.current);
        callTimerIntervalRef.current = null;
      }
      setCallElapsedSeconds(0);
      callSessionStartedAtRef.current = null;
      return;
    }

    if (readOnly && initialPhoneCallId != null) {
      setCallElapsedSeconds(0);
      callSessionStartedAtRef.current = null;
      return;
    }

    callSessionStartedAtRef.current = Date.now();

    const startDelay = window.setTimeout(() => {
      setCallElapsedSeconds(0);
      if (callTimerIntervalRef.current !== null) {
        window.clearInterval(callTimerIntervalRef.current);
        callTimerIntervalRef.current = null;
      }
      callTimerIntervalRef.current = window.setInterval(() => {
        setCallElapsedSeconds((prev) => prev + 1);
      }, 1000);
    }, 500);

    return () => {
      window.clearTimeout(startDelay);
      if (callTimerIntervalRef.current !== null) {
        window.clearInterval(callTimerIntervalRef.current);
        callTimerIntervalRef.current = null;
      }
    };
  }, [open, readOnly, initialPhoneCallId]);

  useEffect(() => {
    if (!open) return;
    const loadData = async () => {
      try {
        setPrivacyLoading(true);
        const notice = await getPrivacyNotice();
        setPrivacyText(notice?.content || '');
        if (initialPhoneCallId == null) {
          setPrivacyVersion(notice?.version || '');
        }
      } catch (error) {
        setPrivacyText('No fue posible cargar el aviso de privacidad en este momento.');
        if (initialPhoneCallId == null) {
          setPrivacyVersion('');
        }
      } finally {
        setPrivacyLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPhoneCallId]);

  useEffect(() => {
    if (!open) return;
    const loadCatalogsAndCompanies = async () => {
      try {
        setCatalogLoading(true);
        const [catalogData, accountsData] = await Promise.all([
          getAllCatalogs(),
          getAvailableAccounts()
        ]);
        setCatalogs(catalogData || {});
        setCompanies(currentCompanies => {
          const activeCompanies = accountsData || [];
          const historicalInactive = currentCompanies.filter(
            current => current.isActive === false && !activeCompanies.some(
              (active: { id?: number; orgId?: string }) =>
                String(active.id || active.orgId || '') === String(current.id || current.orgId || '')
            )
          );
          let merged = [...activeCompanies, ...historicalInactive];
          const prefId = whatsappPrefill?.accountId?.trim();
          if (prefId && !merged.some((c) => String(c.id || c.orgId || '') === prefId)) {
            const numericId = Number(prefId);
            merged = [
              ...merged,
              {
                id: Number.isFinite(numericId) ? numericId : undefined,
                orgId: prefId,
                orgPath: whatsappPrefill?.accountName || `Empresa WhatsApp (${prefId})`,
                isActive: true,
                sessionCount: 0,
              },
            ];
          }
          return merged;
        });
      } catch (error) {
      } finally {
        setCatalogLoading(false);
      }
    };
    loadCatalogsAndCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !draftPhoneCallId) return;

    const mapSurveyRecord = (
      items: ClientSurveyRecord[]
    ): Record<
      number,
      { clientSurveyId: number | null; status: ClientSurveyStatus; answers: Record<number, ClientSurveyAnswer> }
    > => {
      const acc: Record<
        number,
        { clientSurveyId: number | null; status: ClientSurveyStatus; answers: Record<number, ClientSurveyAnswer> }
      > = {};
      items.forEach((item) => {
        const answers: Record<number, ClientSurveyAnswer> = {};
        (item.answers || []).forEach((ans) => {
          answers[ans.surveyQuestionId] = ans;
        });
        acc[item.clientId] = {
          clientSurveyId: item.clientSurveyId ?? null,
          status: item.status,
          answers
        };
      });
      return acc;
    };

    const loadSurveyContext = async () => {
      try {
        setSurveyError('');
        const templates = await getSurveyTemplates(true);
        const activeTemplate = Array.isArray(templates)
          ? templates.find((tpl) => tpl.active) ?? templates[0] ?? null
          : null;
        if (!activeTemplate) {
          setSurveyTemplate(null);
          return;
        }
        const detail = await getSurveyTemplateById(activeTemplate.id);
        setSurveyTemplate(detail);

        const existing = await getClientSurveysByPhoneCall(draftPhoneCallId);
        setClientSurveysByClientId(mapSurveyRecord(existing || []));
      } catch (error) {
        setSurveyError(
          error instanceof Error
            ? error.message
            : 'No se pudo cargar el contexto de encuestas para esta llamada.'
        );
      }
    };

    void loadSurveyContext();
  }, [open, draftPhoneCallId]);

  const employeeDisabled = !privacyAccepted;
  const genderOptions = useMemo(() => catalogs.GENDER || [], [catalogs.GENDER]);
  const ageRangeOptions = useMemo(() => catalogs.AGE_RANGE || [], [catalogs.AGE_RANGE]);
  const lengthOfServiceOptions = useMemo(() => catalogs.LENGTH_OF_SERVICE || [], [catalogs.LENGTH_OF_SERVICE]);
  const maritalStatusOptions = useMemo(() => catalogs.MARITAL_STATUS || [], [catalogs.MARITAL_STATUS]);

  const handleEmployeeChange = (field: keyof EmployeeFormState, value: string) => {
    setEmployeeForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAgeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 2);
    handleEmployeeChange('age', digits);
  };

  const normalizeName = (value: string) => (value || '').trim().toLowerCase();

  const handlePhoneChange = (field: 'mobilePhone' | 'homePhone', value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    handleEmployeeChange(field, digits);
  };

  const employeeLockedByServices = patients.length > 0;
  const employeeSectionActive = !employeeDisabled && !employeeLockedByServices;
  const ageError =
    employeeSectionActive &&
    employeeForm.age !== '' &&
    !isValidAgeOneOrTwoDigits(employeeForm.age);
  const emailError =
    employeeSectionActive && employeeForm.email !== '' && !isValidEmailFormat(employeeForm.email);
  const mobileError =
    employeeSectionActive &&
    employeeForm.mobilePhone !== '' &&
    !isValidPhoneFormat(employeeForm.mobilePhone);
  const homePhoneError =
    employeeSectionActive &&
    employeeForm.homePhone !== '' &&
    !isValidPhoneFormat(employeeForm.homePhone);
  const selectedCompany = useMemo(() => {
    if (!employeeForm.accountId || !Array.isArray(companies)) return null;
    return (
      companies.find(
        (company) => String(company.id || company.orgId || '') === employeeForm.accountId
      ) || null
    );
  }, [companies, employeeForm.accountId]);
  const upsertPersistedClient = useCallback((data: PatientCaptureData, draftClientId?: number | null) => {
    if (typeof draftClientId !== 'number') return;
    setPersistedClients((prev) => {
      const key = getPersistedClientKey({
        relationship: data.relationship,
        firstName: data.firstName,
        lastName: data.lastName,
        middleName: data.middleName,
        email: data.email
      });
      const nextClient: PersistedClient = {
        key,
        draftClientId,
        relationship: data.relationship,
        firstName: data.firstName,
        lastName: data.lastName,
        middleName: data.middleName,
        email: data.email
      };
      const byId = prev.findIndex((item) => item.draftClientId === draftClientId);
      if (byId >= 0) {
        const copy = [...prev];
        copy[byId] = nextClient;
        return copy;
      }
      const byKey = prev.findIndex((item) => item.key === key);
      if (byKey >= 0) {
        const copy = [...prev];
        copy[byKey] = nextClient;
        return copy;
      }
      return [...prev, nextClient];
    });
  }, []);
  const serviceTypeOptions = useMemo(() => catalogs.PRODUCT || [], [catalogs.PRODUCT]);
  const deliveryMethodOptions = useMemo(
    () => catalogs.DELIVERY_METHOD || [],
    [catalogs.DELIVERY_METHOD]
  );

  const getCatalogDescription = useCallback(
    (catalogKey: string, code: string): string => {
      if (!catalogs[catalogKey] || !Array.isArray(catalogs[catalogKey]) || !code) {
        return code || '';
      }
      const item = catalogs[catalogKey].find((entry) => entry.code === code);
      return item?.description || code;
    },
    [catalogs]
  );

  const applyClaimsFromRoot = useCallback((root: Record<string, unknown>) => {
    const extracted = extractClaimsFromPhoneCall(root);
    setReferralClaims(extracted.all);
    setClaimsByReferralId(extracted.byReferralId);
  }, []);

  const reloadClaimsFromPhoneCall = useCallback(async () => {
    if (!draftPhoneCallId) return;
    try {
      const phoneCallData = await getPhoneCallById(draftPhoneCallId);
      const root = extractPhoneCallRoot(phoneCallData);
      applyClaimsFromRoot(root);
      const status = String(root.status || PhoneCallStatus.DRAFT);
      setPhoneCallStatus(status);
      setHadCrisis(Boolean(root.hadCrisis) || String(root.crisisNotes || '').trim() !== '');
    } catch {
      setServiceSyncError('No se pudieron recargar las citas de la llamada.');
    }
  }, [applyClaimsFromRoot, draftPhoneCallId, getPhoneCallById]);

  const callFinalized = phoneCallStatus !== PhoneCallStatus.DRAFT;

  const scopedClaimsForModal = useMemo(() => {
    if (claimsModalReferralId == null) return referralClaims;
    return claimsByReferralId[claimsModalReferralId] ?? [];
  }, [claimsByReferralId, claimsModalReferralId, referralClaims]);

  const enrichedClaimsForModal = useMemo(() => {
    const enrichClaim = (claim: PhoneCallClaim): PhoneCallClaim => {
      const patient = patients.find((row) => row.draftReferralId === claim.referralId);
      if (!patient) return claim;

      const patientName = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim();
      const specialistName =
        claim.assignedSpecialistName?.trim() ||
        patient.assignedSpecialistName?.trim() ||
        (typeof patient.assignedSpecialistId === 'number' && patient.assignedSpecialistId > 0
          ? 'Especialista asignado'
          : undefined);
      return {
        ...claim,
        clientLabel: claim.clientLabel || patientName || undefined,
        serviceType: claim.serviceType || patient.serviceType || undefined,
        assignedSpecialistName: specialistName
      };
    };

    return scopedClaimsForModal.map(enrichClaim);
  }, [patients, scopedClaimsForModal]);

  const openClaimsModal = useCallback((referralId: number | null = null) => {
    setClaimsModalReferralId(referralId);
    setClaimsModalOpen(true);
  }, []);
  const groupedClients = useMemo(() => {
    const groups = new Map<
      string,
      {
        key: string;
        client: PersistedClient;
        rows: Array<{ patient: PatientCaptureData; index: number }>;
      }
    >();

    persistedClients.forEach((client) => {
      groups.set(`id:${client.draftClientId}`, { key: `id:${client.draftClientId}`, client, rows: [] });
    });

    patients.forEach((patient, index) => {
      const idKey = typeof patient.draftClientId === 'number' ? `id:${patient.draftClientId}` : null;
      const key = idKey || `key:${getPatientPersonKey(patient)}`;
      if (!groups.has(key)) {
        const fallbackClient: PersistedClient = {
          key,
          draftClientId: patient.draftClientId ?? -1,
          relationship: patient.relationship,
          firstName: patient.firstName,
          lastName: patient.lastName,
          middleName: patient.middleName,
          email: patient.email
        };
        groups.set(key, { key, client: fallbackClient, rows: [] });
      }
      groups.get(key)!.rows.push({ patient, index });
    });

    return Array.from(groups.values());
  }, [patients, persistedClients]);

  useEffect(() => {
    if (patients.length === 0) return;
    setPersistedClients((prev) => {
      const byId = new Map<number, PersistedClient>();
      prev.forEach((item) => {
        if (item.draftClientId > 0) byId.set(item.draftClientId, item);
      });
      patients.forEach((p) => {
        if (typeof p.draftClientId !== 'number' || p.draftClientId <= 0) return;
        byId.set(p.draftClientId, {
          key: `id:${p.draftClientId}`,
          draftClientId: p.draftClientId,
          relationship: p.relationship,
          firstName: p.firstName,
          lastName: p.lastName,
          middleName: p.middleName,
          email: p.email
        });
      });
      return Array.from(byId.values());
    });
  }, [patients]);
  const canOpenPatientCapture =
    employeeForm.firstName.trim() !== '' &&
    employeeForm.lastName.trim() !== '' &&
    employeeForm.accountId.trim() !== '' &&
    employeeForm.mobilePhone.trim() !== '';
  const hasUnsavedEmployeeChanges =
    employeeSavedSnapshot == null ||
    serializeEmployeeDraftSnapshot(employeeForm, referralSourceIntake) !== employeeSavedSnapshot;
  const canAddService =
    !callFinalized &&
    !readOnly &&
    canOpenPatientCapture &&
    employeeSavedToDraft &&
    referralSourceIntake.trim() !== '' &&
    crisisPendingService == null;
  const severityEditable = phoneCallStatus === PhoneCallStatus.DRAFT && !readOnly;
  /** Encuestas solo para clientes con al menos un servicio capturado como EAP. */
  const eapSurveyClientIds = useMemo(() => collectEapSurveyClientIds(patients), [patients]);

  const surveySubmittedClients = useMemo(
    () =>
      eapSurveyClientIds.filter((clientId) => clientSurveysByClientId[clientId]?.status === 'SUBMITTED')
        .length,
    [eapSurveyClientIds, clientSurveysByClientId]
  );

  const surveyDeclinedClients = useMemo(
    () =>
      eapSurveyClientIds.filter((clientId) => clientSurveysByClientId[clientId]?.status === 'DECLINED')
        .length,
    [eapSurveyClientIds, clientSurveysByClientId]
  );

  const surveyResolvedClients = useMemo(
    () =>
      eapSurveyClientIds.filter((clientId) => {
        const status = clientSurveysByClientId[clientId]?.status;
        return status === 'SUBMITTED' || status === 'DECLINED';
      }).length,
    [eapSurveyClientIds, clientSurveysByClientId]
  );

  const surveyClientsWithAnyAnswer = useMemo(
    () =>
      eapSurveyClientIds.filter(
        (clientId) => Object.keys(clientSurveysByClientId[clientId]?.answers || {}).length > 0
      ).length,
    [eapSurveyClientIds, clientSurveysByClientId]
  );

  const surveyStatus: 'NA' | 'PENDING' | 'PARTIAL' | 'COMPLETED' | 'DECLINED' =
    eapSurveyClientIds.length === 0
      ? 'NA'
      : surveyResolvedClients === eapSurveyClientIds.length &&
          surveySubmittedClients === eapSurveyClientIds.length
        ? 'COMPLETED'
        : surveyResolvedClients === eapSurveyClientIds.length &&
            surveyDeclinedClients === eapSurveyClientIds.length
          ? 'DECLINED'
          : surveyResolvedClients === eapSurveyClientIds.length
            ? 'PARTIAL'
            : surveySubmittedClients > 0 ||
                surveyDeclinedClients > 0 ||
                surveyClientsWithAnyAnswer > 0
              ? 'PARTIAL'
              : 'PENDING';

  const surveysReadyToFinish =
    eapSurveyClientIds.length === 0 || surveyResolvedClients === eapSurveyClientIds.length;

  const canFinishCall =
    phoneCallStatus === PhoneCallStatus.DRAFT &&
    patients.length > 0 &&
    crisisPendingService == null &&
    Boolean(draftPhoneCallId) &&
    surveysReadyToFinish;

  const unchannelizedPatients = useMemo(
    () =>
      patients.filter(
        (p) =>
          p.assignedSpecialistId == null ||
          (typeof p.assignedSpecialistId === 'number' && p.assignedSpecialistId <= 0)
      ),
    [patients]
  );

  const finishCallDisabledReason = phoneCallStatus !== PhoneCallStatus.DRAFT
    ? 'La llamada ya fue finalizada.'
    : crisisPendingService
    ? 'Completa el servicio de crisis pendiente antes de terminar la llamada.'
    : patients.length === 0
      ? 'Agrega al menos un servicio antes de terminar la llamada.'
      : eapSurveyClientIds.length > 0 && !surveysReadyToFinish
        ? 'Completa o declina la encuesta EAP de cada cliente antes de terminar la llamada.'
        : !draftPhoneCallId
          ? 'No hay borrador de llamada activo.'
          : '';

  const surveyAppliesToThisCall = eapSurveyClientIds.length > 0;
  const surveyInteractionEnabled = readOnly || surveyAppliesToThisCall;
  const serviceEditingBlocked = false;

  const activeEapSurveyClientIds = useMemo(
    () =>
      eapSurveyClientIds.filter((clientId) => {
        const status = clientSurveysByClientId[clientId]?.status;
        return status !== 'DECLINED' && status !== 'SUBMITTED';
      }),
    [eapSurveyClientIds, clientSurveysByClientId]
  );

  const canUseQuickSurveyMode = useMemo(
    () =>
      Boolean(surveyTemplate) &&
      surveyInteractionEnabled &&
      activeEapSurveyClientIds.length >= QUICK_SURVEY_MIN_EAP_CLIENTS,
    [surveyTemplate, surveyInteractionEnabled, activeEapSurveyClientIds.length]
  );

  const quickSurveyDisabledReason = useMemo(() => {
    if (!surveyTemplate) return 'No hay plantilla de encuesta activa.';
    if (eapSurveyClientIds.length === 0) return 'No hay clientes con servicio EAP a encuestar.';
    if (!surveyInteractionEnabled) return 'La encuesta aún no está disponible en esta llamada.';
    if (activeEapSurveyClientIds.length < QUICK_SURVEY_MIN_EAP_CLIENTS) {
      return `El modo rápido requiere al menos dos clientes EAP activos (no declinados ni enviados). Activos: ${activeEapSurveyClientIds.length}.`;
    }
    return '';
  }, [
    surveyTemplate,
    eapSurveyClientIds.length,
    surveyInteractionEnabled,
    activeEapSurveyClientIds.length
  ]);

  const capturePatientInitialData = useMemo((): PatientCaptureData | null => {
    if (completingCrisisStub && crisisPendingService) {
      return {
        firstName: '',
        lastName: '',
        middleName: '',
        email: '',
        mobilePhone: '',
        gender: '',
        ageRange: '',
        relationship: '',
        serviceType: 'EAP',
        deliveryMethod: '',
        preDiagnosticNotes: crisisPendingService.preDiagnosticNotes,
        hasPriorPsychologicalTreatment: false,
        assignedSpecialistId: null,
        assignedSpecialistName: null,
        assignmentDate: null
      };
    }
    if (serviceDetailViewOpen && viewingPatientIndex !== null) {
      return patients[viewingPatientIndex] ?? null;
    }
    if (editingPatientIndex !== null) {
      return patients[editingPatientIndex] ?? null;
    }
    return null;
  }, [
    completingCrisisStub,
    crisisPendingService,
    serviceDetailViewOpen,
    viewingPatientIndex,
    editingPatientIndex,
    patients
  ]);

  const selectedSurveyClient = useMemo(() => {
    if (selectedSurveyClientId == null) return null;
    const match = persistedClients.find((client) => client.draftClientId === selectedSurveyClientId);
    if (!match) return null;
    return `${match.firstName} ${match.lastName} ${match.middleName}`.replace(/\s+/g, ' ').trim();
  }, [selectedSurveyClientId, persistedClients]);

  const selectedSurveyInitialAnswers = useMemo(
    () =>
      selectedSurveyClientId != null
        ? clientSurveysByClientId[selectedSurveyClientId]?.answers || {}
        : {},
    [selectedSurveyClientId, clientSurveysByClientId]
  );

  const surveyClients = useMemo(
    () =>
      activeEapSurveyClientIds.map((clientId) => {
        const client = persistedClients.find((p) => p.draftClientId === clientId);
        const displayName = client
          ? `${client.firstName} ${client.lastName} ${client.middleName}`.replace(/\s+/g, ' ').trim()
          : `Cliente #${clientId}`;
        return { clientId, displayName };
      }),
    [activeEapSurveyClientIds, persistedClients]
  );

  const surveyAnswersByClient = useMemo(() => {
    const acc: Record<number, Record<number, ClientSurveyAnswer>> = {};
    eapSurveyClientIds.forEach((clientId) => {
      acc[clientId] = clientSurveysByClientId[clientId]?.answers || {};
    });
    return acc;
  }, [eapSurveyClientIds, clientSurveysByClientId]);

  useEffect(() => {
    if (eapSurveyClientIds.length === 0) {
      setSelectedSurveyClientId(null);
      return;
    }
    if (selectedSurveyClientId == null || !eapSurveyClientIds.includes(selectedSurveyClientId)) {
      setSelectedSurveyClientId(eapSurveyClientIds[0]);
    }
  }, [eapSurveyClientIds, selectedSurveyClientId]);

  const persistEmployeeToDraft = useCallback(async () => {
    if (!draftPhoneCallId) {
      setEmployeeUpdateError('No hay borrador de llamada para guardar.');
      return;
    }
    if (!privacyAccepted) {
      setEmployeeUpdateError('Debes aceptar el aviso de privacidad antes de guardar.');
      return;
    }
    if (!canOpenPatientCapture) {
      setEmployeeUpdateError(
        'Completa nombre completo del empleado, empresa y celular antes de guardar.'
      );
      return;
    }

    const accountIdRaw = employeeForm.accountId.trim();
    const accountIdNum = Number(accountIdRaw);
    if (Number.isNaN(accountIdNum)) {
      setEmployeeUpdateError('El identificador de empresa no es válido para guardar.');
      return;
    }

    const employeePayload = buildDraftEmployeePayload(employeeForm, draftEmployeeId);

    try {
      setEmployeeUpdateError('');
      setEmployeeUpdating(true);
      const response = await updatePhoneCallDraft(draftPhoneCallId, {
        phonecall: {
          employee: employeePayload,
          referralSourceIntake: referralSourceIntake.trim() || undefined
        }
      });
      const newEmpId = response?.phonecall?.employee?.id ?? response?.phonecall?.employeeId;
      if (typeof newEmpId === 'number') {
        setDraftEmployeeId(newEmpId);
      }
      setEmployeeSavedToDraft(true);
      setEmployeeSavedSnapshot(
        serializeEmployeeDraftSnapshot(employeeForm, referralSourceIntake)
      );
    } catch (error) {
      setEmployeeUpdateError('No se pudieron guardar los datos del empleado. Revisa la información e intenta de nuevo.');
    } finally {
      setEmployeeUpdating(false);
    }
  }, [
    draftPhoneCallId,
    draftEmployeeId,
    privacyAccepted,
    canOpenPatientCapture,
    employeeForm,
    referralSourceIntake,
    updatePhoneCallDraft
  ]);

  useEffect(() => {
    if (!open) return;

    const canTriggerAutoSave =
      privacyAccepted &&
      !employeeLockedByServices &&
      draftPhoneCallId != null &&
      canOpenPatientCapture &&
      !employeeUpdating;

    if (!canTriggerAutoSave || employeeSavedToDraft || employeeAutoInitialSaveAttemptedRef.current) {
      return;
    }

    employeeAutoInitialSaveAttemptedRef.current = true;
    void persistEmployeeToDraft();
  }, [
    open,
    draftPhoneCallId,
    privacyAccepted,
    employeeLockedByServices,
    canOpenPatientCapture,
    employeeUpdating,
    employeeSavedToDraft,
    persistEmployeeToDraft
  ]);

  const handleCloseAttempt = () => {
    if (readOnly) {
      onClose(false);
      return;
    }
    // Desde WhatsApp el cierre solo oculta el panel; el borrador sigue ligado a la conversación.
    if (embedded || phoneCallStatus !== PhoneCallStatus.DRAFT) {
      handleConfirmClose();
      return;
    }
    setConfirmCloseDialogOpen(true);
  };
  const handleConfirmClose = () => {
    setConfirmCloseDialogOpen(false);
    setPrivacyAccepted(false);
    setPrivacyModalOpenedOnce(false);
    setEmployeeForm(initialEmployeeForm);
    setReferralSourceIntake('');
    setServiceSyncError('');
    setPatients([]);
    setCrisisPendingService(null);
    setCompletingCrisisStub(false);
    setEditingPatientIndex(null);
    setAssigningServiceIndex(null);
    setAssignmentModalOpen(false);
    setCapturePatientModalOpen(false);
    setServiceDetailViewOpen(false);
    setViewingPatientIndex(null);
    setSurveyModalOpen(false);
    setSurveySaving(false);
    setSurveyError('');
    setSurveyTemplate(null);
    setSelectedSurveyClientId(null);
    setClientSurveysByClientId({});
    setMultiSurveyModalOpen(false);
    setServiceDetailViewOpen(false);
    setViewingPatientIndex(null);
    setReferralClaims([]);
    setClaimsByReferralId({});
    setClaimsModalOpen(false);
    setClaimsModalReferralId(null);
    setFinishCallSuccess('');
    const shouldRefresh = refreshDashboardOnClose;
    setRefreshDashboardOnClose(false);
    onClose(shouldRefresh, draftPhoneCallId);
  };
  const handleCancelClose = () => setConfirmCloseDialogOpen(false);

  const executeFinishCall = async () => {
    if (!draftPhoneCallId) return;
    try {
      setFinishCallSaving(true);
      setFinishCallError('');
      setFinishCallEmailWarnings([]);
      setConfirmFinishUnchannelizedOpen(false);
      const wallElapsed =
        callSessionStartedAtRef.current != null
          ? Math.floor((Date.now() - callSessionStartedAtRef.current) / 1000)
          : 0;
      const secondsToPersist = Math.max(callElapsedSeconds, wallElapsed);
      const durationStr = formatDurationHMS(secondsToPersist);
      await updatePhoneCallDraft(draftPhoneCallId, {
        phonecall: {
          id: draftPhoneCallId,
          duration: durationStr,
          hadCrisis
        }
      });
      const finalizeResult = await finalizePhoneCallDraft(draftPhoneCallId, {
        generateClaims: true,
        duration: durationStr
      });
      const emailWarnings: string[] = Array.isArray(finalizeResult?.specialistEmailWarnings)
        ? finalizeResult.specialistEmailWarnings.filter((w: unknown) => typeof w === 'string' && w.trim())
        : [];
      setFinishCallEmailWarnings(emailWarnings);
      const phoneCallData = await getPhoneCallById(draftPhoneCallId);
      const root = extractPhoneCallRoot(phoneCallData);
      const status = String(root.status || PhoneCallStatus.CAPTURED);
      const extracted = extractClaimsFromPhoneCall(root);
      const externalCaseNumbers = extractExternalCaseNumbers(root);
      setPhoneCallStatus(status);
      setHadCrisis(Boolean(root.hadCrisis) || String(root.crisisNotes || '').trim() !== '');
      setPatients((currentPatients) =>
        currentPatients.map((patient) => {
          const externalReferralId = patient.draftReferralId
            ? externalCaseNumbers.get(patient.draftReferralId)
            : undefined;
          return externalReferralId ? { ...patient, externalReferralId } : patient;
        })
      );
      setReferralClaims(extracted.all);
      setClaimsByReferralId(extracted.byReferralId);
      setRefreshDashboardOnClose(true);
      const emailsSent = Number(finalizeResult?.specialistEmailsSent || 0);
      const successBase =
        extracted.all.length > 0
          ? `Llamada finalizada. Se encontraron ${extracted.all.length} cita(s); puedes gestionar evidencias desde aquí.`
          : 'Llamada finalizada. Puedes gestionar citas y evidencias cuando estén disponibles.';
      setFinishCallSuccess(
        emailsSent > 0
          ? `${successBase} Se envió notificación a ${emailsSent} especialista(s).`
          : successBase
      );
    } catch {
      setFinishCallError(
        'No se pudo finalizar la llamada (duración o cierre del borrador). Revisa la conexión e intenta de nuevo.'
      );
    } finally {
      setFinishCallSaving(false);
    }
  };

  const handleFinishCallClick = () => {
    if (!draftPhoneCallId || !canFinishCall || finishCallSaving) return;
    if (unchannelizedPatients.length > 0) {
      setConfirmFinishUnchannelizedOpen(true);
      return;
    }
    void executeFinishCall();
  };

  const handleMarkCaseReadyClick = (referralId: number) => {
    setCaseReadyReferralId(referralId);
    setCaseReadyError('');
    setConfirmCaseReadyOpen(true);
  };

  const executeMarkCaseReady = async () => {
    if (caseReadyReferralId == null) return;
    setCaseReadySaving(true);
    setCaseReadyError('');
    try {
      await markReferralsReadyForSync([caseReadyReferralId]);
      setPatients((prev) =>
        prev.map((p) =>
          p.draftReferralId === caseReadyReferralId ? { ...p, readyForSync: true } : p
        )
      );
      setConfirmCaseReadyOpen(false);
      setCaseReadyReferralId(null);
    } catch (err: unknown) {
      setCaseReadyError(
        err instanceof Error ? err.message : 'No se pudo marcar el caso para sincronizar.'
      );
    } finally {
      setCaseReadySaving(false);
    }
  };

  const persistPrivacyConsent = async (nextValue: boolean, closeModalAfterSuccess = false) => {
    const previousValue = privacyAccepted;
    setPrivacyUpdateError('');
    setPrivacyAccepted(nextValue);

    if (!draftPhoneCallId) {
      setPrivacyAccepted(previousValue);
      setPrivacyUpdateError('No hay borrador de llamada para guardar el aviso de privacidad.');
      return;
    }

    try {
      setPrivacyUpdating(true);
      await updatePhoneCallDraft(draftPhoneCallId, {
        phonecall: {
          privacyConsent: nextValue,
          ...(privacyVersion ? { privacyVersion } : {})
        }
      });
      if (closeModalAfterSuccess) {
        setPrivacyFullScreenOpen(false);
      }
    } catch (error) {
      setPrivacyAccepted(previousValue);
      setPrivacyUpdateError('No se pudo guardar el aviso de privacidad. Intenta nuevamente.');
    } finally {
      setPrivacyUpdating(false);
    }
  };

  const handleAcceptPrivacyFromModal = () => {
    void persistPrivacyConsent(true, true);
  };

  const persistSeverityLevel = async (nextValue: SeverityLevel) => {
    const previousValue = severityLevel;
    setSeverityUpdateError('');
    setSeverityLevel(nextValue);

    if (!draftPhoneCallId) {
      setSeverityLevel(previousValue);
      setSeverityUpdateError('No hay borrador de llamada para guardar la severidad.');
      return;
    }

    if (phoneCallStatus !== PhoneCallStatus.DRAFT || readOnly) {
      setSeverityLevel(previousValue);
      setSeverityUpdateError('No se puede actualizar el semáforo en este estado.');
      return;
    }

    try {
      setSeverityUpdating(true);
      const response = await updatePhoneCallSeverityLevel(draftPhoneCallId, nextValue);
      const persisted = parseSeverityLevel(extractPhoneCallRoot(response).severityLevel);
      if (persisted) setSeverityLevel(persisted);
    } catch (error) {
      setSeverityLevel(previousValue);
      setSeverityUpdateError(
        error instanceof Error ? error.message : 'No se pudo guardar la severidad. Intenta nuevamente.'
      );
    } finally {
      setSeverityUpdating(false);
    }
  };

  const persistCrisisNotes = useCallback(
    async (text: string): Promise<boolean> => {
      if (!draftPhoneCallId) {
        setCrisisSaveError('No hay borrador de llamada para guardar las notas de crisis.');
        return false;
      }

      try {
        setCrisisSaving(true);
        setCrisisSaveError('');
        const normalized = text.trim();
        await updatePhoneCallDraft(draftPhoneCallId, {
          phonecall: {
            crisisNotes: normalized || null,
            hadCrisis: normalized !== ''
          }
        });
        setCrisisNotes(normalized);
        setHadCrisis(normalized !== '');
        return true;
      } catch {
        setCrisisSaveError('No se pudieron guardar las notas de crisis. Intenta de nuevo.');
        return false;
      } finally {
        setCrisisSaving(false);
      }
    },
    [draftPhoneCallId, updatePhoneCallDraft]
  );

  const handleOpenCrisisModal = () => {
    if (!isNewCallCapture || readOnly) return;
    setCrisisDraftText(crisisNotes);
    setCrisisSaveError('');
    setCrisisModalOpen(true);
  };

  const handleCloseCrisisModal = () => {
    void (async () => {
      if (readOnly) {
        setCrisisModalOpen(false);
        return;
      }
      const saved = await persistCrisisNotes(crisisDraftText);
      if (!saved) return;
      setCrisisModalOpen(false);
      const normalized = crisisDraftText.trim();
      if (normalized) {
        setCrisisPendingService((prev) => {
          if (prev) {
            return { ...prev, preDiagnosticNotes: normalized };
          }
          return createCrisisPendingService(normalized);
        });
        setTimeout(() => {
          servicesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        setCrisisPendingService(null);
      }
    })();
  };

  const handleCompleteCrisisService = () => {
    if (!crisisPendingService) return;
    if (!canOpenPatientCapture) {
      setPatientValidationError(
        'Completa nombre completo del empleado, empresa y celular antes de completar el servicio de crisis.'
      );
      return;
    }
    setPatientValidationError('');
    setCompletingCrisisStub(true);
    setEditingPatientIndex(null);
    setServiceDetailViewOpen(false);
    setViewingPatientIndex(null);
    setCapturePatientModalOpen(true);
  };

  const handleDiscardCrisisPendingService = () => {
    setCrisisPendingService(null);
  };

  const persistSurveyForClient = async (answers: ClientSurveyAnswer[], submit: boolean) => {
    if (!draftPhoneCallId || !surveyTemplate || selectedSurveyClientId == null) return;
    const current = clientSurveysByClientId[selectedSurveyClientId];
    if (current?.status === 'DECLINED') {
      setSurveyError('Esta encuesta fue declinada. Cambia a «Sí, realizar» para continuar.');
      return;
    }
    try {
      setSurveySaving(true);
      setSurveyError('');
      const response = await batchSaveClientSurveys({
        phoneCallId: draftPhoneCallId,
        surveys: [
          {
            clientSurveyId: current?.clientSurveyId ?? null,
            clientId: selectedSurveyClientId,
            surveyTemplateId: surveyTemplate.id,
            status: submit ? 'SUBMITTED' : 'DRAFT',
            answers
          }
        ]
      });
      const saved = response.surveys?.[0];
      if (saved) {
        const nextAnswers: Record<number, ClientSurveyAnswer> = {};
        (saved.answers || []).forEach((answer) => {
          nextAnswers[answer.surveyQuestionId] = answer;
        });
        setClientSurveysByClientId((prev) => ({
          ...prev,
          [selectedSurveyClientId]: {
            clientSurveyId: saved.clientSurveyId ?? current?.clientSurveyId ?? null,
            status: saved.status,
            answers: {
              ...(prev[selectedSurveyClientId]?.answers || {}),
              ...nextAnswers
            }
          }
        }));
      }
      setSurveyModalOpen(false);
    } catch (error) {
      setSurveyError(error instanceof Error ? error.message : 'No se pudo guardar la encuesta.');
    } finally {
      setSurveySaving(false);
    }
  };

  const persistSurveyParticipation = async (
    clientId: number,
    participate: boolean
  ) => {
    if (!draftPhoneCallId || !surveyTemplate || readOnly) return;
    const current = clientSurveysByClientId[clientId];
    if (current?.status === 'SUBMITTED') {
      setSurveyError('La encuesta ya fue enviada; no se puede cambiar a «No realizar».');
      return;
    }
    const nextStatus: ClientSurveyStatus = participate ? 'DRAFT' : 'DECLINED';
    if (current?.status === nextStatus) return;

    try {
      setSurveySaving(true);
      setSurveyError('');
      const answers = participate
        ? Object.values(current?.answers || {})
        : [];
      const response = await batchSaveClientSurveys({
        phoneCallId: draftPhoneCallId,
        surveys: [
          {
            clientSurveyId: current?.clientSurveyId ?? null,
            clientId,
            surveyTemplateId: surveyTemplate.id,
            status: nextStatus,
            answers
          }
        ]
      });
      const saved = response.surveys?.[0];
      setClientSurveysByClientId((prev) => {
        const prevEntry = prev[clientId];
        const nextAnswers: Record<number, ClientSurveyAnswer> = participate
          ? { ...(prevEntry?.answers || {}) }
          : {};
        if (participate && saved?.answers) {
          saved.answers.forEach((answer) => {
            nextAnswers[answer.surveyQuestionId] = answer;
          });
        }
        return {
          ...prev,
          [clientId]: {
            clientSurveyId: saved?.clientSurveyId ?? prevEntry?.clientSurveyId ?? null,
            status: saved?.status ?? nextStatus,
            answers: nextAnswers
          }
        };
      });
    } catch (error) {
      setSurveyError(
        error instanceof Error ? error.message : 'No se pudo actualizar la participación de la encuesta.'
      );
    } finally {
      setSurveySaving(false);
    }
  };

  const persistSurveyBatch = async (
    updates: Array<{ clientId: number; answers: ClientSurveyAnswer[] }>,
    submit: boolean
  ) => {
    if (!draftPhoneCallId || !surveyTemplate || updates.length === 0) return;
    const eligible = updates.filter(
      (item) => clientSurveysByClientId[item.clientId]?.status !== 'DECLINED'
    );
    if (eligible.length === 0) {
      setSurveyError('No hay encuestas activas para guardar (todas declinadas).');
      return;
    }
    try {
      setSurveySaving(true);
      setSurveyError('');
      const response = await batchSaveClientSurveys({
        phoneCallId: draftPhoneCallId,
        surveys: eligible.map((item) => ({
          clientSurveyId: clientSurveysByClientId[item.clientId]?.clientSurveyId ?? null,
          clientId: item.clientId,
          surveyTemplateId: surveyTemplate.id,
          status: submit ? 'SUBMITTED' : 'DRAFT',
          answers: item.answers
        }))
      });

      setClientSurveysByClientId((prev) => {
        const next = { ...prev };
        (response.surveys || []).forEach((saved) => {
          const mergedAnswers = { ...(next[saved.clientId]?.answers || {}) };
          (saved.answers || []).forEach((answer) => {
            mergedAnswers[answer.surveyQuestionId] = answer;
          });
          next[saved.clientId] = {
            clientSurveyId: saved.clientSurveyId ?? next[saved.clientId]?.clientSurveyId ?? null,
            status: saved.status,
            answers: mergedAnswers
          };
        });
        return next;
      });
      if (submit) setMultiSurveyModalOpen(false);
    } catch (error) {
      setSurveyError(error instanceof Error ? error.message : 'No se pudo guardar la encuesta.');
    } finally {
      setSurveySaving(false);
    }
  };

  const intakeDialogTitle = readOnly
    ? 'Ver Intake'
    : embedded
      ? initialPhoneCallId != null
        ? 'Continuar Intake'
        : 'Registrar Intake'
      : initialPhoneCallId != null
        ? 'Editar Intake'
        : 'Registrar Intake';
  const closeActionLabel = embedded ? 'Volver al chat' : 'Cerrar';

  const isNewCallCapture = initialPhoneCallId == null;

  return (
    <>
      <IntakeShell embedded={embedded} open={open} onDismiss={handleCloseAttempt}>
        <DialogTitle sx={embedded ? { flexShrink: 0 } : undefined}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {intakeDialogTitle}
              </Typography>
              {embedded && (
                <Typography variant="caption" color="text.secondary">
                  El chat queda a la izquierda para copiar los datos.
                </Typography>
              )}
            </Box>
            <IconButton
              aria-label={closeActionLabel}
              onClick={handleCloseAttempt}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 3,
            scrollBehavior: 'smooth',
            ...(embedded ? { flex: 1, minHeight: 0, overflow: 'auto' } : {}),
          }}
        >
          {readOnly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Modo solo consulta: no puedes modificar este registro desde aquí.
            </Alert>
          )}
          {finishCallSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFinishCallSuccess('')}>
              {finishCallSuccess}
            </Alert>
          )}
          {finishCallEmailWarnings.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              onClose={() => setFinishCallEmailWarnings([])}
            >
              {finishCallEmailWarnings.length === 1
                ? finishCallEmailWarnings[0]
                : (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {finishCallEmailWarnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </Box>
                )}
            </Alert>
          )}
          <fieldset
            disabled={readOnly}
            style={{
              border: 'none',
              margin: 0,
              padding: 0,
              minWidth: 0,
              width: '100%'
            }}
          >
          <Paper
            elevation={0}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 8,
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50'
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, lineHeight: 1.2, textTransform: 'capitalize' }}
                  >
                    {headerReferenceDate ? formatHeaderCallDateCdmx(headerReferenceDate) : '—'}
                  </Typography>
                  {headerReferenceDate && (
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      {formatHeaderTimeCdmx(headerReferenceDate)}
                    </Typography>
                  )}
                  <SeveritySemaphoreSelector
                    value={severityLevel}
                    onChange={(nextValue) => {
                      void persistSeverityLevel(nextValue);
                    }}
                    disabled={!severityEditable || !draftPhoneCallId || severityUpdating}
                    loading={severityUpdating}
                  />
                  {hadCrisis && (
                    <Chip
                      label="Crisis registrada"
                      color="error"
                      size="small"
                      icon={<WarningAmberIcon />}
                      variant="filled"
                    />
                  )}
                  {!readOnly && isNewCallCapture && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<WarningAmberIcon />}
                      onClick={handleOpenCrisisModal}
                      disabled={!draftPhoneCallId || crisisSaving}
                      sx={{
                        fontWeight: 700,
                        alignSelf: 'center',
                        bgcolor: 'error.main',
                        color: 'common.white',
                        boxShadow: 2,
                        '& .MuiButton-startIcon': {
                          color: 'warning.main'
                        },
                        '&:hover': {
                          bgcolor: 'error.dark'
                        }
                      }}
                    >
                      Crisis
                    </Button>
                  )}
                </Stack>
                {severityUpdateError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {severityUpdateError}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                {initialPhoneCallId != null ? (
                  <Stack spacing={0.25} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                    <Typography variant="caption" color="text.secondary">
                      Duración de la llamada
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        letterSpacing: 1,
                        color: 'primary.main',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      {savedPhoneCallDuration || '—'}
                    </Typography>
                    {!readOnly && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                          Contador de esta sesión
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            letterSpacing: 0.5,
                            color: 'text.secondary',
                            fontVariantNumeric: 'tabular-nums'
                          }}
                        >
                          {formatDurationHMS(callElapsedSeconds)}
                        </Typography>
                      </>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={0.25} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                    <Typography variant="caption" color="text.secondary">
                      Duración de esta sesión
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        letterSpacing: 1,
                        color: 'primary.main',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      {formatDurationHMS(callElapsedSeconds)}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Paper>
          {finishCallError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFinishCallError('')}>
              {finishCallError}
            </Alert>
          )}
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} lg={8} sx={{ pr: { lg: 1 } }}>
              <Stack spacing={3}>
                <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Aviso de Privacidad</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {draftCreationError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {draftCreationError}
                    </Alert>
                  )}
                  {privacyUpdateError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {privacyUpdateError}
                    </Alert>
                  )}
                  {privacyLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} /><Typography variant="body2">Cargando aviso de privacidad...</Typography></Box>
                  ) : (
                    <Stack spacing={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setPrivacyModalOpenedOnce(true);
                          setPrivacyFullScreenOpen(true);
                        }}
                      >
                        Ver aviso de privacidad
                      </Button>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={privacyAccepted}
                            onChange={(e) => {
                              void persistPrivacyConsent(e.target.checked);
                            }}
                            disabled={privacyUpdating || !draftPhoneCallId}
                          />
                        }
                        label="Confirmo que el aviso de privacidad fue leído y aceptado."
                      />
                    </Stack>
                  )}
                </Paper>

                <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', opacity: employeeDisabled ? 0.7 : 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Datos del Empleado</Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  {employeeUpdateError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {employeeUpdateError}
                    </Alert>
                  )}
                  {employeeUpdating && employeeSectionActive && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">
                          {draftEmployeeId === null
                            ? 'Guardando datos del empleado...'
                            : 'Actualizando datos del empleado...'}
                        </Typography>
                      </Box>
                    </Alert>
                  )}
                  {employeeDisabled && <Alert severity="warning" sx={{ mb: 2 }}>Debes aceptar el aviso de privacidad para continuar con este step.</Alert>}
                  {employeeLockedByServices && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      El empleado está bloqueado mientras existan servicios. Elimina todos los servicios para volver a editarlo.
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        required
                        label="Nombre"
                        value={employeeForm.firstName}
                        onChange={(e) => handleEmployeeChange('firstName', e.target.value)}
                        disabled={employeeDisabled || employeeLockedByServices}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        required
                        label="Apellido Paterno"
                        value={employeeForm.lastName}
                        onChange={(e) => handleEmployeeChange('lastName', e.target.value)}
                        disabled={employeeDisabled || employeeLockedByServices}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Apellido Materno"
                        value={employeeForm.middleName}
                        onChange={(e) => handleEmployeeChange('middleName', e.target.value)}
                        disabled={employeeDisabled || employeeLockedByServices}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Edad (opcional)"
                        value={employeeForm.age}
                        onChange={(e) => handleAgeChange(e.target.value)}
                        disabled={employeeDisabled || employeeLockedByServices}
                        inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                        error={ageError}
                        helperText={
                          ageError ? 'La edad debe ser de 1 o 2 dígitos (ej. 5 o 25).' : undefined
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth disabled={employeeDisabled || employeeLockedByServices || catalogLoading}>
                        <InputLabel>Género (opcional)</InputLabel>
                        <Select
                          label="Género (opcional)"
                          value={employeeForm.gender}
                          onChange={(e) => handleEmployeeChange('gender', e.target.value)}
                        >
                          {genderOptions.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                              {item.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.5}>
                        <Autocomplete
                          options={Array.isArray(companies) ? companies : []}
                          getOptionLabel={(option) => option?.orgPath || option?.orgId || ''}
                          getOptionDisabled={(option) => option?.isActive === false}
                          isOptionEqualToValue={(option, value) =>
                            String(option.id || option.orgId || '') === String(value.id || value.orgId || '')
                          }
                          value={
                            Array.isArray(companies) && employeeForm.accountId
                              ? companies.find(
                                  (company) => String(company.id || company.orgId || '') === employeeForm.accountId
                                ) || null
                              : null
                          }
                          onChange={(_, newValue) => {
                            handleEmployeeChange(
                              'accountId',
                              newValue ? String(newValue.id || newValue.orgId || '') : ''
                            );
                          }}
                          disabled={
                            employeeDisabled ||
                            employeeLockedByServices ||
                            catalogLoading ||
                            companies.length === 0 ||
                            Boolean(whatsappPrefill?.lockAccount)
                          }
                          disablePortal
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              label="Empresa"
                              placeholder="Seleccionar empresa..."
                              helperText={
                                whatsappPrefill?.lockAccount
                                  ? 'Empresa fijada por el contacto de WhatsApp'
                                  : undefined
                              }
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {option.orgPath || option.orgId || 'Empresa sin nombre'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.sessionCount ?? 0} sesiones asignadas
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        />
                        {selectedCompany && (
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
                            Sesiones asignadas: {selectedCompany.sessionCount ?? 0}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Ciudad (opcional)"
                        value={employeeForm.city}
                        onChange={(e) => handleEmployeeChange('city', e.target.value)}
                        disabled={employeeDisabled || employeeLockedByServices}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="email"
                        label="Email (opcional)"
                        value={employeeForm.email}
                        onChange={(e) => handleEmployeeChange('email', e.target.value)}
                        disabled={employeeDisabled || employeeLockedByServices}
                        error={emailError}
                        helperText={
                          emailError
                            ? 'Ingrese un correo con formato válido (ej: correo@dominio.com).'
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        required
                        label="Celular"
                        value={employeeForm.mobilePhone}
                        onChange={(e) => handlePhoneChange('mobilePhone', e.target.value)}
                        disabled={employeeDisabled || employeeLockedByServices}
                        inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                        error={mobileError}
                        helperText={mobileError ? 'Debe tener exactamente 10 dígitos.' : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Teléfono de casa (opcional)"
                        value={employeeForm.homePhone}
                        onChange={(e) => handlePhoneChange('homePhone', e.target.value)}
                        disabled={employeeDisabled || employeeLockedByServices}
                        inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                        error={homePhoneError}
                        helperText={
                          homePhoneError
                            ? 'Si captura teléfono de casa, debe tener exactamente 10 dígitos.'
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Puesto (opcional)"
                        value={employeeForm.position}
                        onChange={(e) => handleEmployeeChange('position', e.target.value)}
                        disabled={employeeDisabled || employeeLockedByServices}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth disabled={employeeDisabled || employeeLockedByServices || catalogLoading}>
                        <InputLabel>Tiempo de servicio (opcional)</InputLabel>
                        <Select
                          label="Tiempo de servicio (opcional)"
                          value={employeeForm.lengthOfService}
                          onChange={(e) => handleEmployeeChange('lengthOfService', e.target.value)}
                        >
                          {lengthOfServiceOptions.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                              {item.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth disabled={employeeDisabled || employeeLockedByServices || catalogLoading}>
                        <InputLabel>Estado civil (opcional)</InputLabel>
                        <Select
                          label="Estado civil (opcional)"
                          value={employeeForm.maritalStatus}
                          onChange={(e) => handleEmployeeChange('maritalStatus', e.target.value)}
                        >
                          {maritalStatusOptions.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                              {item.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="¿Cómo se enteró del programa?"
                        placeholder="Describe cómo se enteró del programa…"
                        value={referralSourceIntake}
                        onChange={(e) => setReferralSourceIntake(e.target.value)}
                        onBlur={() => {
                          if (!draftPhoneCallId || readOnly || phoneCallStatus !== PhoneCallStatus.DRAFT) return;
                          void updatePhoneCallDraft(draftPhoneCallId, {
                            phonecall: {
                              referralSourceIntake: referralSourceIntake.trim() || undefined
                            }
                          }).catch(() => {
                            setEmployeeUpdateError(
                              'No se pudo guardar cómo se enteró del programa. Intenta de nuevo al guardar el empleado.'
                            );
                          });
                        }}
                        disabled={employeeDisabled || employeeLockedByServices || readOnly}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Tooltip
                      title={
                        employeeSavedToDraft && !hasUnsavedEmployeeChanges
                          ? 'Empleado guardado. Edita algún campo para habilitar de nuevo.'
                          : employeeSavedToDraft
                            ? 'Guardar cambios adicionales del empleado en el borrador'
                            : canOpenPatientCapture && draftPhoneCallId
                              ? 'La primera vez se guarda solo al tener los datos listos; también puedes pulsar aquí.'
                              : 'Completa nombre completo, empresa y celular; confirma privacidad y espera el borrador de llamada.'
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          onClick={() => void persistEmployeeToDraft()}
                          disabled={
                            employeeDisabled ||
                            employeeLockedByServices ||
                            !draftPhoneCallId ||
                            employeeUpdating ||
                            !canOpenPatientCapture ||
                            !hasUnsavedEmployeeChanges
                          }
                        >
                          Guardar empleado
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Paper>

                <Paper
                  ref={servicesSectionRef}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'wrap',
                      mb: 0.5
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Servicios agregados
                    </Typography>
                    <Tooltip
                      title={
                        callFinalized
                          ? 'La llamada ya fue finalizada.'
                          : readOnly
                            ? 'La captura está en solo lectura.'
                            : crisisPendingService
                              ? 'Completa el servicio de crisis pendiente antes de agregar otros servicios.'
                              : canAddService
                                ? 'Agregar servicio'
                                : !canOpenPatientCapture
                                  ? 'Completa nombre completo del empleado, empresa y celular.'
                                  : !employeeSavedToDraft
                                    ? 'Espera el primer guardado automático del empleado o pulsa «Guardar empleado».'
                                    : 'Completa «¿Cómo se enteró del programa?» en los datos del empleado.'
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          onClick={() => {
                            if (callFinalized || readOnly || crisisPendingService) return;
                            setPatientValidationError('');
                            setEditingPatientIndex(null);
                            setServiceDetailViewOpen(false);
                            setViewingPatientIndex(null);
                            setCapturePatientModalOpen(true);
                          }}
                          disabled={!canAddService}
                        >
                          Agregar Servicio
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  {crisisPendingService && isNewCallCapture && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderColor: 'error.light',
                        bgcolor: 'rgba(211, 47, 47, 0.06)'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 1,
                          flexWrap: 'wrap',
                          mb: 1
                        }}
                      >
                        <Stack spacing={0.75}>
                          <Chip
                            label="Servicio por crisis — pendiente"
                            color="error"
                            size="small"
                            icon={<WarningAmberIcon />}
                            sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Servicio:{' '}
                            {serviceTypeOptions.find((option) => option.code === crisisPendingService.serviceType)
                              ?.description || crisisPendingService.serviceType}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Completa beneficiario y datos del servicio para generar el servicio en el borrador.
                          </Typography>
                        </Stack>
                        {!readOnly && (
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Editar notas de crisis">
                              <IconButton
                                size="small"
                                onClick={handleOpenCrisisModal}
                                disabled={crisisSaving}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Quitar servicio pendiente (las notas de crisis se conservan en la llamada)">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleDiscardCrisisPendingService}
                                  disabled={serviceEditingBlocked}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        )}
                      </Box>
                      <Box
                        sx={{
                          maxHeight: 140,
                          overflowY: 'auto',
                          mb: 1.5,
                          p: 1.25,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {crisisPendingService.preDiagnosticNotes}
                        </Typography>
                      </Box>
                      {!readOnly && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip
                            title={
                              canOpenPatientCapture
                                ? 'Completar servicio de crisis'
                                : 'Completa nombre completo del empleado, empresa y celular primero'
                            }
                          >
                            <span>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={handleCompleteCrisisService}
                                disabled={serviceEditingBlocked}
                                sx={{ fontWeight: 700 }}
                              >
                                Completar servicio
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </Paper>
                  )}
                  {serviceSyncError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServiceSyncError('')}>
                      {serviceSyncError}
                    </Alert>
                  )}
                  {groupedClients.length === 0 && !crisisPendingService ? (
                    <Typography variant="body2" color="text.secondary">
                      Aún no has agregado servicios.
                    </Typography>
                  ) : groupedClients.length > 0 ? (
                    <Stack spacing={1.5}>
                      {groupedClients.map((group) => {
                        const header = group.client;
                        const groupClientId = group.client.draftClientId > 0 ? group.client.draftClientId : null;
                        return (
                          <Paper key={group.key} variant="outlined" sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {`${header.firstName} ${header.lastName} ${header.middleName}`.replace(/\s+/g, ' ').trim()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                  Relación: {getRelationshipLabel(header.relationship, group.rows[0]?.patient.otherRelationshipDescription)}
                                </Typography>
                              </Box>
                              <Tooltip
                                title={
                                  callFinalized
                                    ? 'La llamada ya fue finalizada.'
                                    : readOnly
                                      ? 'La captura está en solo lectura.'
                                      : 'Eliminar client completo (con todos sus servicios)'
                                }
                              >
                                <span>
                                  <IconButton
                                    color="error"
                                    disabled={
                                      callFinalized ||
                                      readOnly ||
                                      !draftPhoneCallId ||
                                      !groupClientId ||
                                      serviceEditingBlocked
                                    }
                                    onClick={() => {
                                      if (callFinalized || readOnly) return;
                                      void (async () => {
                                        try {
                                          setServiceSyncError('');
                                          if (draftPhoneCallId && groupClientId) {
                                            await updatePhoneCallDraft(draftPhoneCallId, {
                                              phonecall: {
                                                patients: [
                                                  {
                                                    operation: 'DELETE',
                                                    client: { id: groupClientId }
                                                  }
                                                ]
                                              }
                                            });
                                          }
                                          setPatients((prev) =>
                                            prev.filter((row) => row.draftClientId !== groupClientId)
                                          );
                                          setPersistedClients((prev) =>
                                            prev.filter((row) => row.draftClientId !== groupClientId)
                                          );
                                        } catch {
                                          setServiceSyncError(
                                            'No se pudo eliminar el client completo del borrador.'
                                          );
                                        }
                                      })();
                                    }}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>

                            <Stack spacing={1}>
                              {group.rows.length === 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  Este client no tiene servicios actualmente.
                                </Typography>
                              )}
                              {group.rows.map(({ patient, index }) => (
                                <Paper key={`${group.key}-${index}`} variant="outlined" sx={{ p: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Número de caso: {patient.draftReferralId ?? 'N/D'}
                                        {' | Referral: '}
                                        {patient.externalReferralId ?? 'Pendiente'}
                                        {' | Servicio: '}
                                        {serviceTypeOptions.find((option) => option.code === patient.serviceType)?.description ||
                                          patient.serviceType ||
                                          'N/D'}
                                        {patient.deliveryMethod
                                          ? ` | Entrega: ${
                                              deliveryMethodOptions.find(
                                                (option) => option.code === patient.deliveryMethod
                                              )?.description || patient.deliveryMethod
                                            }`
                                          : ''}{' '}
                                        | Especialista: {patient.assignedSpecialistName || 'Sin asignar'}
                                        {isHwbWellBeingCoachingServiceType(patient.serviceType) &&
                                        patient.pregnancyCoaching
                                          ? ' | Pregnancy coaching'
                                          : ''}
                                      </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.5}>
                                      <Tooltip title="Ver detalle del servicio">
                                        <span>
                                          <IconButton
                                            color="default"
                                            aria-label="Ver detalle del servicio"
                                            onClick={() => {
                                              setPatientValidationError('');
                                              setCapturePatientModalOpen(false);
                                              setEditingPatientIndex(null);
                                              setViewingPatientIndex(index);
                                              setServiceDetailViewOpen(true);
                                            }}
                                          >
                                            <VisibilityOutlinedIcon />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Canalización">
                                        <span>
                                          <IconButton
                                            color={patient.assignedSpecialistId ? 'success' : 'error'}
                                            disabled={serviceEditingBlocked}
                                            onClick={() => {
                                              setAssigningServiceIndex(index);
                                              setAssignmentModalOpen(true);
                                            }}
                                          >
                                            <AssignmentIndOutlinedIcon />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      {callFinalized && !readOnly && typeof patient.draftReferralId === 'number' && (
                                        <Tooltip title="Citas y evidencias del servicio">
                                          <span>
                                            <IconButton
                                              color="secondary"
                                              onClick={() => openClaimsModal(patient.draftReferralId as number)}
                                            >
                                              <AttachFileOutlinedIcon />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      )}
                                      {callFinalized &&
                                        !readOnly &&
                                        typeof patient.draftReferralId === 'number' &&
                                        patient.assignedSpecialistId &&
                                        !patient.readyForSync &&
                                        !patient.externalReferralId && (
                                          <Tooltip title="Marcar caso para Sincronización ComPsych">
                                            <span>
                                              <IconButton
                                                color="warning"
                                                onClick={() =>
                                                  handleMarkCaseReadyClick(patient.draftReferralId as number)
                                                }
                                              >
                                                <SyncOutlinedIcon />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        )}
                                      {patient.readyForSync && (
                                        <Chip
                                          label="Enviado a Sync"
                                          size="small"
                                          color="warning"
                                          sx={{ height: 24, fontSize: '0.7rem' }}
                                        />
                                      )}
                                      <Tooltip title="Modificar servicio">
                                        <span>
                                          <IconButton
                                            color="primary"
                                            disabled={serviceEditingBlocked}
                                            onClick={() => {
                                              setPatientValidationError('');
                                              setServiceDetailViewOpen(false);
                                              setViewingPatientIndex(null);
                                              setEditingPatientIndex(index);
                                              setCapturePatientModalOpen(true);
                                            }}
                                          >
                                            <EditOutlinedIcon />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip
                                        title={
                                          callFinalized
                                            ? 'La llamada ya fue finalizada.'
                                            : readOnly
                                              ? 'La captura está en solo lectura.'
                                              : 'Eliminar servicio'
                                        }
                                      >
                                        <span>
                                          <IconButton
                                            color="error"
                                            disabled={callFinalized || readOnly || serviceEditingBlocked}
                                            onClick={() => {
                                              if (callFinalized || readOnly) return;
                                              void (async () => {
                                                const patientRow = patients[index];
                                                try {
                                                  setServiceSyncError('');
                                                  if (draftPhoneCallId && patientRow.draftClientId && patientRow.draftReferralId) {
                                                    await updatePhoneCallDraft(draftPhoneCallId, {
                                                      phonecall: {
                                                        patients: [
                                                          {
                                                            operation: 'DELETE',
                                                            client: { id: patientRow.draftClientId as number },
                                                            referralId: patientRow.draftReferralId as number
                                                          }
                                                        ]
                                                      }
                                                    });
                                                  }
                                                  setPatients((prev) =>
                                                    prev.filter((_, patientIndex) => patientIndex !== index)
                                                  );
                                                } catch (error) {
                                                  setServiceSyncError(
                                                    'No se pudo eliminar el servicio del borrador.'
                                                  );
                                                }
                                              })();
                                            }}
                                          >
                                            <DeleteOutlineIcon />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                  </Box>
                                </Paper>
                              ))}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  ) : null}
                </Paper>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    opacity: surveyAppliesToThisCall ? 1 : 0.72
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Encuesta
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  {!readOnly && !surveyAppliesToThisCall && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      Esta sección se habilitará cuando captures un servicio EAP.
                    </Alert>
                  )}
                  {surveyStatus === 'COMPLETED' ? (
                    <Alert severity="success" sx={{ mb: 1.5 }}>
                      Estado: Completada ({surveySubmittedClients}/{eapSurveyClientIds.length} enviadas).
                    </Alert>
                  ) : surveyStatus === 'DECLINED' ? (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      Estado: Declinada ({surveyDeclinedClients}/{eapSurveyClientIds.length}).
                    </Alert>
                  ) : surveyStatus === 'PARTIAL' ? (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      Estado: Parcial ({surveySubmittedClients} enviadas, {surveyDeclinedClients}{' '}
                      declinadas, {eapSurveyClientIds.length - surveyResolvedClients} pendientes).
                    </Alert>
                  ) : surveyStatus === 'PENDING' ? (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      Estado: Pendiente.
                    </Alert>
                  ) : null}
                  {surveyError && (
                    <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSurveyError('')}>
                      {surveyError}
                    </Alert>
                  )}
                  <FormControl fullWidth sx={{ mb: 1 }} disabled={!surveyInteractionEnabled || eapSurveyClientIds.length === 0}>
                    <InputLabel>Cliente a encuestar</InputLabel>
                    <Select
                      label="Cliente a encuestar"
                      value={selectedSurveyClientId ?? ''}
                      onChange={(e) => setSelectedSurveyClientId(Number(e.target.value))}
                      renderValue={(value) => {
                        const clientId = Number(value);
                        const client = persistedClients.find((p) => p.draftClientId === clientId);
                        const label = client
                          ? `${client.firstName} ${client.lastName} ${client.middleName}`
                              .replace(/\s+/g, ' ')
                              .trim()
                          : `Cliente #${clientId}`;
                        const chip = getSurveyClientStatusChip(clientSurveysByClientId[clientId]);
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                              {label}
                            </Typography>
                            <Chip size="small" label={chip.label} color={chip.color} />
                          </Box>
                        );
                      }}
                    >
                      {eapSurveyClientIds.map((clientId) => {
                        const client = persistedClients.find((p) => p.draftClientId === clientId);
                        const label = client
                          ? `${client.firstName} ${client.lastName} ${client.middleName}`
                              .replace(/\s+/g, ' ')
                              .trim()
                          : `Cliente #${clientId}`;
                        const chip = getSurveyClientStatusChip(clientSurveysByClientId[clientId]);
                        return (
                          <MenuItem key={clientId} value={clientId}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                gap: 1
                              }}
                            >
                              <Typography variant="body2">{label}</Typography>
                              <Chip size="small" label={chip.label} color={chip.color} />
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {surveyAppliesToThisCall && selectedSurveyClientId != null && (
                    <FormControl
                      component="fieldset"
                      sx={{ mb: 1.5 }}
                      disabled={
                        readOnly ||
                        !surveyTemplate ||
                        surveySaving ||
                        clientSurveysByClientId[selectedSurveyClientId]?.status === 'SUBMITTED' ||
                        !surveyInteractionEnabled
                      }
                    >
                      <RadioGroup
                        row
                        value={
                          clientSurveysByClientId[selectedSurveyClientId]?.status === 'DECLINED'
                            ? 'no'
                            : 'yes'
                        }
                        onChange={(e) => {
                          const nextYes = e.target.value === 'yes';
                          void persistSurveyParticipation(selectedSurveyClientId, nextYes);
                        }}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio size="small" />}
                          label="Sí, realizar"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio size="small" />}
                          label="No realizar"
                          disabled={
                            clientSurveysByClientId[selectedSurveyClientId]?.status === 'SUBMITTED'
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <Tooltip
                      disableHoverListener={!quickSurveyDisabledReason}
                      title={quickSurveyDisabledReason}
                    >
                      <span>
                        <Button
                          variant="contained"
                          disabled={!canUseQuickSurveyMode}
                          onClick={() => setMultiSurveyModalOpen(true)}
                        >
                          Modo rápido por pregunta
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      disabled={
                        !surveyTemplate ||
                        selectedSurveyClientId == null ||
                        !surveyInteractionEnabled ||
                        clientSurveysByClientId[selectedSurveyClientId]?.status === 'DECLINED'
                      }
                      onClick={() => setSurveyModalOpen(true)}
                    >
                      {clientSurveysByClientId[selectedSurveyClientId ?? -1]?.status === 'SUBMITTED'
                        ? 'Revisar encuesta'
                        : clientSurveysByClientId[selectedSurveyClientId ?? -1]?.status === 'DECLINED'
                          ? 'Encuesta declinada'
                          : surveyStatus === 'PARTIAL'
                            ? 'Continuar encuesta'
                            : 'Responder encuesta'}
                    </Button>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Stack
                spacing={2}
                sx={{
                  position: 'sticky',
                  top: 16,
                  alignSelf: 'flex-start',
                  zIndex: 1
                }}
              >
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                {draftPhoneCallId && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    Llamada en borrador: #{draftPhoneCallId}
                  </Alert>
                )}
                {!privacyAccepted ? (
                  <Stack spacing={1.5}>
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                      Guía de atención
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Aviso de Privacidad
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Abre y lee el aviso con la persona. Cuando confirme la aceptación, se habilitará la guía para la captura de datos del empleado.
                    </Typography>
                  </Stack>
                ) : !canOpenPatientCapture ? (
                  <Stack spacing={1.5}>
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                      Guía de atención
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Datos del Empleado
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {"🎙️ Inicio cálido y presentación:\n¡Hola, Muy buen día!, mi nombre es ( N O M B R E ), gracias por comunicarse al Programa de Asistencia al Empleado, ¿En qué le puedo apoyar?\n📌 Si la persona dice que busca terapia:\n¡Perfecto, con gusto le ayudamos! Tomaré unos datos y procedemos a dar de alta su servicio, ¿le parece bien?"}
                    </Typography>
                  </Stack>
                ) : !employeeSavedToDraft ? (
                  <Stack spacing={1.5}>
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                      Guía de atención
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Guardar empleado
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Al tener listos los datos obligatorios y el borrador de la llamada, el empleado se guarda solo la primera vez. Si cambias datos después, usa «Guardar empleado» para actualizar el borrador.
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                      Guía de atención
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Motivo de consulta
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {"💬 Motivo de consulta:\n- Y para poder asignarle al terapeuta más adecuado, ¿me puede contar brevemente qué le está motivando a buscar apoyo?\n  (Si la persona se extiende demasiado)\n“Aprecio mucho que me comparta esto, vamos a acompañarle paso a paso. Por ahora, con que me dé una idea general está perfecto, en sesión podrá abordar el tema con calma y con más detalle.”"}
                    </Typography>
                  </Stack>
                )}
              </Paper>
              {crisisNotes.trim() !== '' && (
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'error.light',
                    bgcolor: 'rgba(211, 47, 47, 0.06)'
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <WarningAmberIcon color="error" fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'error.dark' }}>
                      Notas de crisis
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      maxHeight: 220,
                      overflowY: 'auto',
                      pr: 0.5,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 1.5
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {crisisNotes}
                    </Typography>
                  </Box>
                </Paper>
              )}
              </Stack>
            </Grid>
          </Grid>
        </fieldset>
        </DialogContent>
        <DialogActions sx={{ p: 2, ...(embedded ? { flexShrink: 0 } : {}) }}>
          {phoneCallStatus === PhoneCallStatus.DRAFT && (
            <Tooltip disableHoverListener={!finishCallDisabledReason} title={finishCallDisabledReason}>
              <span>
                <Button
                  variant="contained"
                  color="success"
                  disabled={readOnly || !canFinishCall || finishCallSaving}
                  onClick={handleFinishCallClick}
            startIcon={finishCallSaving ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {finishCallSaving ? 'Guardando…' : 'Terminar llamada'}
          </Button>
            </span>
          </Tooltip>
          )}
          {callFinalized && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AssignmentOutlinedIcon />}
              onClick={() => openClaimsModal(null)}
            >
              Citas y evidencias ({referralClaims.length})
            </Button>
          )}
          <Button variant="outlined" onClick={handleCloseAttempt}>{closeActionLabel}</Button>
        </DialogActions>
      </IntakeShell>

      <Dialog
        open={crisisModalOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleCloseCrisisModal();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Atención de crisis
          <IconButton
            aria-label="cerrar"
            onClick={handleCloseCrisisModal}
            disabled={crisisSaving}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Captura lo sucedido durante la atención de crisis. Al cerrar, el texto se guardará en la
            llamada y quedará visible debajo de la guía de atención.
          </Typography>
          {crisisSaveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCrisisSaveError('')}>
              {crisisSaveError}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={12}
            label="Descripción de la crisis"
            placeholder="Describe lo ocurrido, acciones tomadas y estado actual del usuario..."
            value={crisisDraftText}
            onChange={(e) => setCrisisDraftText(e.target.value)}
            disabled={readOnly || crisisSaving}
            InputProps={{
              readOnly,
              sx: {
                alignItems: 'flex-start',
                maxHeight: 360,
                overflow: 'auto'
              }
            }}
            inputProps={{
              style: { overflow: 'auto' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={handleCloseCrisisModal}
            disabled={crisisSaving}
            startIcon={crisisSaving ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {readOnly ? 'Cerrar' : crisisSaving ? 'Guardando…' : 'Guardar y cerrar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmCloseDialogOpen} onClose={handleCancelClose} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar cierre</DialogTitle>
        <DialogContent>
          <Typography>
            Los últimos cambios podrían perderse si no se han guardado. ¿Desea continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelClose}>Cancelar</Button>
          <Button
            onClick={handleConfirmClose}
            color="error"
            variant="contained"
            sx={{ bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
          >
            Cerrar de todos modos
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmFinishUnchannelizedOpen}
        onClose={() => !finishCallSaving && setConfirmFinishUnchannelizedOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Servicios sin canalizar</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            Tienes {unchannelizedPatients.length} servicio
            {unchannelizedPatients.length !== 1 ? 's' : ''} sin canalizar a un especialista.
            ¿Deseas continuar y terminar la llamada de todos modos?
            <Box component="span" sx={{ display: 'block', fontWeight: 700, mt: 1 }}>
              Podrás canalizarlo{unchannelizedPatients.length !== 1 ? 's' : ''} o editarlo
              {unchannelizedPatients.length !== 1 ? 's' : ''} después de finalizar la llamada.
            </Box>
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {unchannelizedPatients.map((patient, idx) => (
              <Typography component="li" variant="body2" key={`unchannelized-${idx}`} sx={{ mb: 0.5 }}>
                {(patient.firstName || '').trim()} {(patient.lastName || '').trim()}
                {patient.serviceType
                  ? ` — ${
                      serviceTypeOptions.find((o) => o.code === patient.serviceType)?.description ||
                      patient.serviceType
                    }`
                  : ''}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmFinishUnchannelizedOpen(false)}
            disabled={finishCallSaving}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => void executeFinishCall()}
            color="warning"
            variant="contained"
            disabled={finishCallSaving}
            startIcon={finishCallSaving ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {finishCallSaving ? 'Guardando…' : 'Continuar y terminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmCaseReadyOpen}
        onClose={() => !caseReadySaving && setConfirmCaseReadyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Marcar caso para Sync</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción <strong>no se puede deshacer</strong>. El caso aparecerá para
            Sincronización con ComPsych
          </Alert>
          <Typography>
            ¿Confirmas marcar el caso #{caseReadyReferralId} para Sync?
          </Typography>
          {caseReadyError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCaseReadyError('')}>
              {caseReadyError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmCaseReadyOpen(false)} disabled={caseReadySaving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void executeMarkCaseReady()}
            disabled={caseReadySaving}
            startIcon={
              caseReadySaving ? <CircularProgress size={18} color="inherit" /> : <SyncOutlinedIcon />
            }
          >
            {caseReadySaving ? 'Marcando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={privacyFullScreenOpen}
        onClose={() => setPrivacyFullScreenOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Aviso de Privacidad
          <IconButton aria-label="close" onClick={() => setPrivacyFullScreenOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {privacyText || 'Aviso no disponible.'}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPrivacyFullScreenOpen(false)} color="inherit">
            Cerrar
          </Button>
          <Button onClick={handleAcceptPrivacyFromModal} variant="contained">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <CapturePatientModal
        open={capturePatientModalOpen || serviceDetailViewOpen}
        readOnly={serviceDetailViewOpen}
        onClose={() => {
          if (serviceDetailViewOpen) {
            setServiceDetailViewOpen(false);
            setViewingPatientIndex(null);
          } else {
            setCapturePatientModalOpen(false);
            setEditingPatientIndex(null);
            setCompletingCrisisStub(false);
            setPatientValidationError('');
          }
        }}
        onSave={async (data) => {
          const crisisText = crisisNotes.trim() || crisisPendingService?.preDiagnosticNotes || '';
          const dataToSave: PatientCaptureData =
            completingCrisisStub
              ? {
                  ...data,
                  serviceType: 'EAP',
                  preDiagnosticNotes: crisisText || data.preDiagnosticNotes || ''
                }
              : data;

          const duplicatedServiceForPerson = patients.some((item, index) => {
            if (editingPatientIndex !== null && index === editingPatientIndex) return false;

            const samePerson =
              normalizeName(item.firstName) === normalizeName(dataToSave.firstName) &&
              normalizeName(item.lastName) === normalizeName(dataToSave.lastName) &&
              normalizeName(item.middleName) === normalizeName(dataToSave.middleName);

            const sameServiceType =
              (item.serviceType || '').trim() === (dataToSave.serviceType || '').trim();

            return samePerson && sameServiceType;
          });

          if (duplicatedServiceForPerson) {
            setPatientValidationError('Este tipo de servicio ya fue asignado para esta persona.');
            throw new Error('duplicate service');
          }

          if (!draftPhoneCallId) {
            setPatientValidationError('No hay borrador de llamada para guardar el servicio.');
            throw new Error('no draft');
          }

          setPatientValidationError('');
          setServiceSyncError('');

          const editIdx = completingCrisisStub ? null : editingPatientIndex;
          const referralPart = buildDraftReferralPayload(dataToSave, catalogs);

          try {
            if (editIdx !== null) {
              const prev = patients[editIdx];
              if (!prev.draftClientId || !prev.draftReferralId) {
                setPatientValidationError(
                  'Este servicio no tiene vínculo con el borrador; recarga o vuelve a crear el servicio.'
                );
                throw new Error('missing draft ids');
              }

              const response = await updatePhoneCallDraft(draftPhoneCallId, {
                phonecall: {
                  employee: buildDraftEmployeePayload(employeeForm, draftEmployeeId),
                  patients: [
                    {
                      operation: 'UPDATE',
                      client: {
                        ...buildDraftClientPayload(dataToSave),
                        id: prev.draftClientId,
                        referrals: [
                          {
                            id: prev.draftReferralId,
                            ...referralPart,
                            product: dataToSave.serviceType
                          }
                        ]
                      }
                    }
                  ]
                }
              });

              const ids = extractDraftClientReferralIds(
                response,
                dataToSave.firstName,
                dataToSave.lastName,
                dataToSave.middleName,
                dataToSave.relationship,
                dataToSave.email,
                dataToSave.serviceType
              );

              const merged: PatientCaptureData = {
                ...dataToSave,
                draftClientId: ids.clientId ?? prev.draftClientId,
                draftReferralId: ids.referralId ?? prev.draftReferralId
              };

              upsertPersistedClient(merged, merged.draftClientId);
              setPatients((prevList) =>
                prevList.map((item, index) => (index === editIdx ? merged : item))
              );
            } else {
              const personKey = getPatientPersonKey(dataToSave);
              const existingClient = persistedClients.find(
                (p) => getPersistedClientKey(p) === personKey && typeof p.draftClientId === 'number'
              );

              if (existingClient?.draftClientId) {
                const response = await updatePhoneCallDraft(draftPhoneCallId, {
                  phonecall: {
                    employee: buildDraftEmployeePayload(employeeForm, draftEmployeeId),
                    patients: [
                      {
                        operation: 'CREATE',
                        client: applyClientTreatmentFieldsForExistingClient(
                          existingClient.draftClientId,
                          dataToSave
                        ),
                        referral: referralPart
                      }
                    ]
                  }
                });

                const referralId =
                  extractReferralIdFromClient(
                    response,
                    existingClient.draftClientId,
                    dataToSave.serviceType
                  );

                const merged: PatientCaptureData = {
                  ...dataToSave,
                  draftClientId: existingClient.draftClientId,
                  draftReferralId: referralId
                };

                upsertPersistedClient(merged, existingClient.draftClientId);
                setPatients((prevList) => [...prevList, merged]);
              } else {
                const response = await updatePhoneCallDraft(draftPhoneCallId, {
                  phonecall: {
                    employee: buildDraftEmployeePayload(employeeForm, draftEmployeeId),
                    patients: [
                      {
                        operation: 'CREATE',
                        client: buildDraftClientPayload(dataToSave),
                        referral: referralPart
                      }
                    ]
                  }
                });

                const ids = extractDraftClientReferralIds(
                  response,
                  dataToSave.firstName,
                  dataToSave.lastName,
                  dataToSave.middleName,
                  dataToSave.relationship,
                  dataToSave.email,
                  dataToSave.serviceType
                );

                const merged: PatientCaptureData = {
                  ...dataToSave,
                  draftClientId: ids.clientId ?? null,
                  draftReferralId: ids.referralId ?? null
                };

                upsertPersistedClient(merged, merged.draftClientId);
                setPatients((prevList) => [...prevList, merged]);
              }
            }

            if (completingCrisisStub) {
              setCrisisPendingService(null);
              setCompletingCrisisStub(false);
            }

            setEditingPatientIndex(null);
            setCapturePatientModalOpen(false);
            setTimeout(() => {
              servicesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
          } catch (error) {
            setPatientValidationError(
              'No se pudo guardar el servicio en el borrador. Intenta de nuevo.'
            );
            throw error;
          }
        }}
        employeeData={{
          firstName: employeeForm.firstName,
          lastName: employeeForm.lastName,
          middleName: employeeForm.middleName,
          email: employeeForm.email,
          mobilePhone: employeeForm.mobilePhone,
          age: employeeForm.age,
          gender: employeeForm.gender
        }}
        serviceTypeOptions={serviceTypeOptions}
        deliveryMethodOptions={deliveryMethodOptions}
        genderOptions={genderOptions}
        ageRangeOptions={ageRangeOptions}
        existingPatients={patients}
        initialData={capturePatientInitialData}
        validationError={patientValidationError}
        lockServiceType={completingCrisisStub}
      />

      <SurveyModal
        open={surveyModalOpen}
        template={surveyTemplate}
        clientDisplayName={selectedSurveyClient || ''}
        initialAnswers={selectedSurveyInitialAnswers}
        submitted={
          selectedSurveyClientId != null &&
          clientSurveysByClientId[selectedSurveyClientId]?.status === 'SUBMITTED'
        }
        saving={surveySaving}
        error={surveyError}
        onClose={() => setSurveyModalOpen(false)}
        onSave={(answers, submit) => {
          void persistSurveyForClient(answers, submit);
        }}
      />

      <MultiClientSurveyModal
        open={multiSurveyModalOpen}
        template={surveyTemplate}
        clients={surveyClients}
        initialAnswersByClient={surveyAnswersByClient}
        saving={surveySaving}
        error={surveyError}
        onClose={() => setMultiSurveyModalOpen(false)}
        onSaveBatch={(updates, submit) => {
          void persistSurveyBatch(updates, submit);
        }}
      />

      <ClaimsModal
        open={claimsModalOpen}
        onClose={() => {
          setClaimsModalOpen(false);
          setClaimsModalReferralId(null);
        }}
        claims={enrichedClaimsForModal}
        viewOnly={readOnly}
        getCatalogDescription={getCatalogDescription}
        onClaimUpdate={() => {
          void reloadClaimsFromPhoneCall();
        }}
      />

      <ServiceSpecialistAssignmentModal
        open={assignmentModalOpen}
        onClose={() => {
          setAssignmentModalOpen(false);
          setAssigningServiceIndex(null);
        }}
        serviceType={
          assigningServiceIndex !== null ? patients[assigningServiceIndex]?.serviceType || '' : ''
        }
        serviceTypeDescription={
          assigningServiceIndex !== null
            ? serviceTypeOptions.find(
                (option) => option.code === (patients[assigningServiceIndex]?.serviceType || '')
              )?.description || ''
            : ''
        }
        deliveryMethod={
          assigningServiceIndex !== null
            ? patients[assigningServiceIndex]?.deliveryMethod || null
            : null
        }
        deliveryMethodOptions={deliveryMethodOptions}
        selectedSpecialistId={
          assigningServiceIndex !== null
            ? patients[assigningServiceIndex]?.assignedSpecialistId || null
            : null
        }
        onAssign={(specialist: Specialist) => {
          void (async () => {
            if (serviceEditingBlocked) return;
            if (assigningServiceIndex === null) return;
            const idx = assigningServiceIndex;
            const patientRow = patients[idx];
            try {
              setServiceSyncError('');
              setFinishCallEmailWarnings([]);
              if (draftPhoneCallId && patientRow.draftReferralId) {
                const assignResult = await assignSpecialistToDraftReferral(
                  draftPhoneCallId,
                  patientRow.draftReferralId,
                  specialist.specialistId
                );
                const emailWarnings: string[] = Array.isArray(assignResult?.specialistEmailWarnings)
                  ? assignResult.specialistEmailWarnings.filter(
                      (w: unknown) => typeof w === 'string' && String(w).trim()
                    )
                  : [];
                if (emailWarnings.length > 0) {
                  setFinishCallEmailWarnings(emailWarnings);
                }
              }
              setPatients((prev) =>
                prev.map((item, index) =>
                  index === idx
                    ? {
                        ...item,
                        assignedSpecialistId: specialist.specialistId,
                        assignedSpecialistName: specialist.fullName,
                        assignmentDate: new Date().toISOString()
                      }
                    : item
                )
              );
              setAssignmentModalOpen(false);
              setAssigningServiceIndex(null);
            } catch (error) {
              setServiceSyncError('No se pudo asignar el especialista en el borrador.');
            }
          })();
        }}
      />
    </>
  );
};

export default CaptureWizardModal;
